import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardHeaderBtn,
  CardBody,
} from "./../../components/card/card.jsx";
import { FINANCIERAS } from "./../../components/filtros-solicitudes/filtros-solicitudes.jsx";
import Swal from "sweetalert2";
import { Circles } from "react-loader-spinner";
import "moment/locale/es";

const ACCESS_TOKEN =
  "1MhYphhrGUbgbdRy7cyYFGQlXS7tAi6ooUoptkHQbOm9GcsKiZM7uMF1MERxwMWq30I7Y3";

const API_BASE_URL = "https://api.checkmark.mx/";

const METODO_CONTACTO_OPTIONS = [
  "Redes sociales",
  "Punto de venta",
  "Carabana",
  "Volanteo",
  "Recomendación",
  "Publicidad en la calle",
];

const ORIGEN_CLIENTE_OPTIONS = ["Cartera", "Piso", "Digital", "Calle"];

// Única fuente de verdad para las financieras permitidas: el catálogo
// compartido FINANCIERAS (filtros-solicitudes.jsx), el mismo que usan
// Solicitudes (update.js) y los filtros. El select se genera desde aquí y
// solo estos valores se pueden guardar.
const FINANCIERA_OPTIONS = FINANCIERAS.map((f) => f.label);

const INITIAL_CLIENTE = {
  nombre: "",
  apellidoPaterno: "",
  apellidoMaterno: "",
  telefono: "",
  correo: "",
};

const INITIAL_SELECTED_VEHICLE = {
  brand: "",
  model: "",
  ano: "",
  version: "",
  agency: "",
};

const INITIAL_FINANCIAMIENTO = {
  precioVehiculo: "",
  enganche: "",
  tasa: "",
  mensualidad: "",
  plazo: "",
  financiera: "",
  planFinanciamiento: "",
  metodoContacto: "",
  origenCliente: "",
  aseguradora: "",
  montoSeguro: "",
  tipoCompraSeguro: "",
  montoComisionApertura: "",
  tipoComision: "",
  incluyePlacas: "No",
  montoPlacas: "",
  respuestaPruebaManejo: "",
  userId: "",
  asesor: "",
};

// ---------------------------------------------------------------------------
// Solicitud tardía
// ---------------------------------------------------------------------------
// Solo se habilita UN día: el día 1 de cada mes. Sirve para capturar
// solicitudes que corresponden al mes anterior y no se registraron a tiempo.
// La solicitud se contabiliza en el ÚLTIMO DÍA del mes anterior
// (fecha_creacion) y la fecha real de captura se guarda aparte
// (fecha_registro_tardia). Debe coincidir con DIA_HABILITACION_SOLICITUD_TARDIA
// de financiamiento_function.php (el backend vuelve a validar).
const DIA_HABILITACION_SOLICITUD_TARDIA = 1;

// SOLO PARA PRUEBAS: simula el "hoy" (formato "YYYY-MM-DD", ej. "2026-11-01").
// Dejar en "" en producción. Debe coincidir con SOLICITUD_TARDIA_HOY_PRUEBA
// de financiamiento_function.php.
const FECHA_PRUEBA_SOLICITUD_TARDIA = "";

const getHoySolicitudTardia = () => {
  if (FECHA_PRUEBA_SOLICITUD_TARDIA) {
    const [y, m, d] = FECHA_PRUEBA_SOLICITUD_TARDIA.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date();
};

const pad2 = (n) => String(n).padStart(2, "0");
const toYmd = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const esDiaSolicitudTardia = (hoy = getHoySolicitudTardia()) =>
  hoy.getDate() === DIA_HABILITACION_SOLICITUD_TARDIA;

// Única fecha permitida: el último día del mes anterior
// (min = max para que el calendario solo deje elegir ese día)
const getRangoMesAnterior = (hoy = getHoySolicitudTardia()) => {
  const fin = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
  return {
    min: toYmd(fin),
    max: toYmd(fin),
    nombreMes: fin.toLocaleDateString("es-MX", {
      month: "long",
      year: "numeric",
    }),
    etiqueta: fin.toLocaleDateString("es-MX", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
  };
};

const normalizarTexto = (texto = "") =>
  String(texto || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\-_\/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const distanciaLevenshtein = (a = "", b = "") => {
  const s = normalizarTexto(a);
  const t = normalizarTexto(b);

  if (s === t) return 0;
  if (!s.length) return t.length;
  if (!t.length) return s.length;

  const prev = Array.from({ length: t.length + 1 }, (_, i) => i);
  const curr = new Array(t.length + 1).fill(0);

  for (let i = 1; i <= s.length; i += 1) {
    curr[0] = i;

    for (let j = 1; j <= t.length; j += 1) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;

      curr[j] = Math.min(
        curr[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + cost,
      );
    }

    for (let j = 0; j <= t.length; j += 1) {
      prev[j] = curr[j];
    }
  }

  return prev[t.length];
};

// =====================================================================
// Coincidencia flexible de MODELO / VERSIÓN para accesorios y garantías
// (mismo bloque en request.js, crear_venta.js y customer-order-v2.js)
// ---------------------------------------------------------------------
// Convención en BD: "PRODUCTO - MODELO1, MODELO2"  (sin " - " = aplica a todos)
// Cada modelo se convierte a una "llave" compacta, así da igual cómo se escriba:
//   "X-Trail", "X TRAIL", "XTRAIL", "x-trail 2026"  -> "xtrail"
//   "Kicks Play", "KICKSPLAY"                       -> "kicksplay"
//   "X-Trail e-POWER", "X-Trail Hybrid"             -> "xtrailhibrido"
//   "Frontier PR04X", "FRONTIER PRO-4X"             -> "frontierpro4x"
//   "MG 5", "MG5"                                   -> "mg5"
// En garantías se puede listar por versión: "LOCALIZADOR 1 AÑO - Frontier LE, March Exclusive"
// =====================================================================
const ALIAS_TOKENS_MODELO = {
  epower: "hibrido",
  hybrid: "hibrido",
  hibrido: "hibrido",
  hev: "hibrido",
  ehev: "hibrido", // Honda "e:HEV"
  pr04x: "pro4x",
};

const MARCAS_CONOCIDAS_MODELO = [
  "nissan", "renault", "honda", "mg", "seat", "cupra", "volkswagen", "vw", "chirey",
];

const normalizarTextoModelo = (texto = "") =>
  String(texto || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\-_/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const distanciaModelo = (a = "", b = "") => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const curr = [i];
    for (let j = 1; j <= b.length; j += 1) {
      curr[j] = Math.min(
        curr[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    prev = curr;
  }
  return prev[b.length];
};

// Modelo -> tokens limpios (sin años, sin marca, con alias)
const tokensModelo = (texto = "", marca = "") => {
  const marcasQuitar = new Set([
    ...MARCAS_CONOCIDAS_MODELO,
    ...normalizarTextoModelo(marca).split(" ").filter(Boolean),
  ]);

  const crudos = normalizarTextoModelo(texto)
    .replace(/\be\s+power\b/g, "epower") // "e-POWER" -> "e power" -> "epower"
    .split(" ")
    .map((t) => t.replace(/[^a-z0-9]/g, ""))
    .filter(Boolean)
    .filter((t) => !/^(20\d{2}|2[4-9])$/.test(t)); // años: 2026, 26...

  const tokens = [];
  for (let i = 0; i < crudos.length; i += 1) {
    const t = crudos[i];
    if (marcasQuitar.has(t)) {
      // "MG 5" -> "mg5": si a la marca le sigue solo un número, es parte del modelo
      if (/^\d+$/.test(crudos[i + 1] || "")) {
        tokens.push(t + crudos[i + 1]);
        i += 1;
      }
      continue;
    }
    tokens.push(ALIAS_TOKENS_MODELO[t] || t);
  }
  return tokens;
};

const llaveModelo = (texto = "", marca = "") => tokensModelo(texto, marca).join("");

// Llaves de modelo del título "Nombre - Mod1, Mod2". null = sin lista (aplica a todos)
const llavesModeloDeItem = (item, marca = "") => {
  const title = String(item?.title || "");
  if (!title.includes(" - ")) return null;
  const [, rawModelos = ""] = title.split(" - ");
  return rawModelos
    .split(",")
    .map((m) => llaveModelo(m, marca))
    .filter(Boolean);
};

const soloDigitosModelo = (texto = "") => String(texto).replace(/\D/g, "");

// Busca en el catálogo la llave que corresponde a lo capturado. "" si no hay.
// 1) exacta  1b) lo capturado es el inicio de un solo modelo ("kicksplay" -> "kicksplayhibrido")
// 2) lo capturado EMPIEZA con la llave (gana la más larga: "kicksplay" > "kicks")
// 3) la llave aparece completa dentro de lo capturado ("Golf GTI" -> "gti")
// 4) error de dedo (Levenshtein) solo si los números coinciden ("Tiggo 9" ≠ "Tiggo 7")
const buscarModeloCatalogo = (capturado = "", llavesCatalogo = [], marca = "") => {
  const tokens = tokensModelo(capturado, marca);
  const llave = tokens.join("");
  if (!llave) return "";

  const catalogo = Array.from(new Set(llavesCatalogo)).filter(
    (k) => k && k !== "todos",
  );
  if (catalogo.includes(llave)) return llave;

  const masLarga = (lista) => lista.sort((x, y) => y.length - x.length)[0];

  // Lo capturado es el inicio de UN solo modelo del catálogo:
  // "KICKSPLAY" -> "Kicks Play e-POWER", "Frontier V6" -> "Frontier V6 Pro-4X"
  const porExtension = catalogo.filter((k) => k.startsWith(llave));
  if (llave.length >= 4 && porExtension.length === 1) return porExtension[0];

  const porPrefijo = catalogo.filter((k) => k.length >= 2 && llave.startsWith(k));
  if (porPrefijo.length) return masLarga(porPrefijo);

  const segmentos = new Set();
  for (let i = 0; i < tokens.length; i += 1) {
    for (let j = i + 1; j <= tokens.length; j += 1) {
      segmentos.add(tokens.slice(i, j).join(""));
    }
  }
  const porSegmento = catalogo.filter((k) => k.length >= 2 && segmentos.has(k));
  if (porSegmento.length) return masLarga(porSegmento);

  if (llave.length >= 5) {
    const tolerancia = llave.length >= 6 ? 2 : 1;
    const porDistancia = catalogo
      .filter((k) => soloDigitosModelo(k) === soloDigitosModelo(llave))
      .map((k) => ({ k, d: distanciaModelo(k, llave) }))
      .filter(({ d }) => d <= tolerancia)
      .sort((x, y) => x.d - y.d || y.k.length - x.k.length);
    if (porDistancia.length) return porDistancia[0].k;
  }
  return "";
};

// Une modelo + versión sin repetir el modelo cuando la versión ya lo trae:
//   "Frontier" + "Frontier Se TM" -> "frontier se tm"   (no "frontier frontier se tm")
//   "NP300" + "Np300 Pick Up TM Ac" -> "np300 pick up tm ac"
const textoModeloVersion = (modelo = "", version = "", marca = "") => {
  const tm = tokensModelo(modelo, marca);
  let tv = tokensModelo(version, marca);
  const llaveM = tm.join("");

  if (llaveM) {
    let acumulado = "";
    for (let i = 0; i < tv.length; i += 1) {
      acumulado += tv[i];
      if (acumulado === llaveM) {
        tv = tv.slice(i + 1);
        break;
      }
      if (!llaveM.startsWith(acumulado)) break;
    }
  }

  return [...tm, ...tv].join(" ");
};

const esItemGarantia = (item) =>
  normalizarTextoModelo(item?.type || item?.tipo || "") === "garantias";

// Crea el filtro de modelo/versión para un auto.
// items: catálogo YA filtrado por marca y agencia (accesorios + garantías),
//        idealmente junto con los vehículos nuevos (type "nuevos").
// Regresa (item) => boolean.
//  - Accesorios: por modelo. Si la versión trae el sub-modelo (p. ej. "e-POWER",
//    "PRO-4X", "Play") se toma en cuenta: modelo "X-Trail" + versión "e-POWER" -> "xtrailhibrido".
//  - Garantías: por modelo o por modelo+versión ("Frontier LE"). Si no hay versión
//    capturada, se muestran también las garantías por versión de ese modelo.
const crearFiltroModeloAccesorios = (
  items = [],
  { marca = "", modelo = "", version = "" } = {},
) => {
  const llavesAcc = [];
  const llavesGar = [];
  const llavesModelos = []; // modelos reales del catálogo de nuevos
  (Array.isArray(items) ? items : []).forEach((item) => {
    // Vehículos nuevos (babilonia_nuevos): el nombre del modelo viene en
    // "description". Sirven para distinguir modelos que empiezan igual:
    // "Kicks Play e-POWER" ≠ "Kicks", "X-Trail e-POWER" ≠ "X-Trail",
    // "Frontier V6 Pro-4X" ≠ "Frontier".
    if (normalizarTextoModelo(item?.type || "") === "nuevos") {
      const llave = llaveModelo(item?.description || "", marca);
      if (llave) llavesModelos.push(llave);
      return;
    }
    const lista = llavesModeloDeItem(item, marca);
    if (!lista) return;
    (esItemGarantia(item) ? llavesGar : llavesAcc).push(...lista);
  });

  const catalogoBase = [
    ...(llavesAcc.length ? llavesAcc : llavesGar),
    ...llavesModelos,
  ];
  const modeloVersionTxt = textoModeloVersion(modelo, version, marca);
  const sinVersion = !normalizarTextoModelo(version);

  // Si el modelo capturado ES exactamente un modelo conocido (en accesorios o
  // garantías), se respeta: "Kicks Play e-POWER" no cae en "Kicks" ni
  // "Frontier V6 Pro-4X" en "Frontier". Solo se usa la versión si da un
  // sub-modelo más específico del mismo ("Frontier" + "Pro-4X" -> "frontierpro4x").
  const llaveSoloModelo = llaveModelo(modelo, marca);
  const modeloExacto = [...llavesAcc, ...llavesGar].includes(llaveSoloModelo)
    ? llaveSoloModelo
    : "";
  const porVersion = buscarModeloCatalogo(modeloVersionTxt, catalogoBase, marca);

  const modeloBase =
    (porVersion &&
    (!modeloExacto ||
      (porVersion.length > modeloExacto.length &&
        porVersion.startsWith(modeloExacto)))
      ? porVersion
      : "") ||
    modeloExacto ||
    buscarModeloCatalogo(modelo, catalogoBase, marca) ||
    llaveSoloModelo;

  const candidatosGarantia = new Set(
    [
      modeloBase,
      buscarModeloCatalogo(modelo, llavesGar, marca),
      buscarModeloCatalogo(modeloVersionTxt, llavesGar, marca),
    ].filter(Boolean),
  );

  const catalogoSinVersion = [...catalogoBase, modeloBase];

  return (item) => {
    const lista = llavesModeloDeItem(item, marca);
    if (!lista) return true; // sin lista de modelos: aplica a todos
    if (lista.includes("todos")) return true;
    if (!modeloBase) return false;

    if (!esItemGarantia(item)) return lista.includes(modeloBase);

    if (lista.some((k) => candidatosGarantia.has(k))) return true;

    // Sin versión capturada: "Frontier LE" cuenta como "Frontier",
    // pero "Kicks Play" NO cuenta como "Kicks".
    if (sinVersion) {
      return lista.some(
        (k) =>
          k.startsWith(modeloBase) &&
          buscarModeloCatalogo(k, catalogoSinVersion) === modeloBase,
      );
    }
    return false;
  };
};
// =====================================================================

// Monto sugerido de placas según modelo/versión.
// registros: filas type = 'placas' de la agencia (endpoint /financiamiento/placas),
// p. ej. "PLACAS EDO MEX - TODOS" ($4,300) y "PLACAS EDO MEX - Frontier, NP300, Urvan" ($6,500).
// Gana el registro que nombra al modelo; "TODOS" queda como respaldo. null = sin sugerencia.
const obtenerMontoPlacasPorModelo = (
  registros = [],
  { marca = "", modelo = "", version = "" } = {},
) => {
  const lista = (Array.isArray(registros) ? registros : []).map((r) => ({
    ...r,
    type: "placas", // se evalúa como accesorio (por modelo)
  }));
  if (!lista.length || !normalizarTextoModelo(modelo)) return null;

  const coincide = crearFiltroModeloAccesorios(lista, { marca, modelo, version });
  const candidatos = lista.filter(coincide);
  if (!candidatos.length) return null;

  const especifico = candidatos.find((r) => {
    const llaves = llavesModeloDeItem(r, marca);
    return llaves && !llaves.includes("todos");
  });
  const elegido = especifico || candidatos[0];

  const precio = Number(String(elegido?.price ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(precio) && precio > 0 ? String(Math.round(precio * 100) / 100) : null;
};

const coincideBusquedaAproximada = (texto = "", busqueda = "") => {
  const haystack = normalizarTexto(texto);
  const query = normalizarTexto(busqueda);

  if (!query) return true;
  if (!haystack) return false;
  if (haystack.includes(query)) return true;

  const palabrasTexto = haystack.split(" ").filter(Boolean);
  const palabrasBusqueda = query.split(" ").filter(Boolean);

  return palabrasBusqueda.every((palabraBusqueda) => {
    if (haystack.includes(palabraBusqueda)) return true;

    return palabrasTexto.some((palabraTexto) => {
      if (
        palabraTexto.includes(palabraBusqueda) ||
        palabraBusqueda.includes(palabraTexto)
      ) {
        return true;
      }

      if (palabraBusqueda.length < 4 || palabraTexto.length < 4) return false;

      const tolerancia =
        Math.max(palabraBusqueda.length, palabraTexto.length) >= 8 ? 2 : 1;

      return distanciaLevenshtein(palabraBusqueda, palabraTexto) <= tolerancia;
    });
  });
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;

  const cleanValue = String(value)
    .replace(/,/g, "")
    .replace(/\$/g, "")
    .replace(/%/g, "")
    .trim();

  const numberValue = Number(cleanValue.replace(/[^0-9.-]/g, ""));

  return Number.isFinite(numberValue) ? numberValue : 0;
};

const cleanNumberInput = (value) => {
  const cleanValue = String(value || "")
    .replace(/,/g, "")
    .replace(/\$/g, "")
    .replace(/%/g, "")
    .replace(/[^0-9.]/g, "");

  const parts = cleanValue.split(".");

  if (parts.length <= 1) return cleanValue;

  return `${parts[0]}.${parts.slice(1).join("")}`;
};

const formatThousands = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const cleanValue = cleanNumberInput(value);
  if (!cleanValue) return "";

  const [integerPart, decimalPart] = cleanValue.split(".");
  const formattedInteger = Number(integerPart || 0).toLocaleString("es-MX");

  if (cleanValue.endsWith(".")) return `${formattedInteger}.`;
  if (decimalPart !== undefined) return `${formattedInteger}.${decimalPart}`;

  return formattedInteger;
};

const formatCurrency = (value) => {
  const numberValue = toNumber(value);

  return numberValue.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
  });
};

const cleanName = (name = "") =>
  String(name || "")
    .trim()
    .replace(/^(ma|m\.a\.|ma\.)\s+/i, "")
    .trim();

const formatName = (name = "") =>
  String(name || "")
    .toLowerCase()
    .replace(/(^\p{L}|\s\p{L})/gu, (char) => char.toUpperCase());

const separarNombreCompleto = (nombreCompleto = "") => {
  const limpio = formatName(cleanName(nombreCompleto))
    .replace(/\s+/g, " ")
    .trim();

  if (!limpio) {
    return {
      nombre: "",
      apellidoPaterno: "",
      apellidoMaterno: "",
    };
  }

  const partes = limpio.split(" ").filter(Boolean);

  // Convención mexicana como respaldo:
  // 4+ palabras => las últimas dos se consideran apellidos.
  // 3 palabras  => nombre + paterno + materno.
  // 2 palabras  => nombre + paterno.
  // La IA puede devolver los tres campos explícitamente y tendrá prioridad.
  if (partes.length >= 3) {
    return {
      nombre: partes.slice(0, -2).join(" "),
      apellidoPaterno: partes[partes.length - 2],
      apellidoMaterno: partes[partes.length - 1],
    };
  }

  if (partes.length === 2) {
    return {
      nombre: partes[0],
      apellidoPaterno: partes[1],
      apellidoMaterno: "",
    };
  }

  return {
    nombre: partes[0],
    apellidoPaterno: "",
    apellidoMaterno: "",
  };
};

const quitarAcentos = (texto = "") => {
  const mapaAcentos = {
    á: "a",
    é: "e",
    í: "i",
    ó: "o",
    ú: "u",
    ü: "u",
    ñ: "n",
    Á: "A",
    É: "E",
    Í: "I",
    Ó: "O",
    Ú: "U",
    Ü: "U",
    Ñ: "N",
  };

  return String(texto || "").replace(
    /([áéíóúüñÁÉÍÓÚÜÑ])/g,
    (match) => mapaAcentos[match] || match,
  );
};

const limpiarJsonOpenAI = (texto = "") =>
  String(texto || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .trim();

const parseOpenAIJson = (texto = "") => {
  const limpio = limpiarJsonOpenAI(texto);

  try {
    const parsed = JSON.parse(limpio);
    return parsed?.datos || parsed || {};
  } catch (error) {
    console.warn("No se pudo parsear JSON directo de OpenAI:", error);
  }

  return {};
};

const limpiarValorOCR = (value = "") =>
  String(value || "")
    .replace(/\s+/g, " ")
    .replace(/^[\s:;|.-]+|[\s:;|.-]+$/g, "")
    .trim();

const firstRegexValue = (texto = "", regexList = [], group = 1) => {
  for (const regex of regexList) {
    const match = String(texto || "").match(regex);
    if (match?.[group]) return limpiarValorOCR(match[group]);
  }
  return "";
};

const normalizarFinanciera = (value = "") => {
  const n = normalizarTexto(value);

  if (!n) return "";
  if (n.includes("nr finance") || n.includes("credi nissan") || n.includes("nrfm")) return "NR Finance";
  if (n.includes("banorte") || n.includes("mercantil del norte")) return "Banorte";
  if (n.includes("bbva")) return "BBVA";
  if (n.includes("inbursa") || n.includes("autoexpress")) return "Inbursa";
  if (n.includes("hey banco") || n === "hey") return "Hey Banco";
  if (n.includes("kuna")) return "Kuna";
  if (n.includes("nexu")) return "Nexu";
  if (n.includes("santander")) return "Santander";
  if (n.includes("scotiabank")) return "Scotiabank";
  if (n.includes("hsbc")) return "HSBC";
  if (n.includes("cibanco")) return "Cibanco";
  if (n.includes("sicrea")) return "Sicrea";
  if (n.includes("cetelem")) return "Cetelem";
  if (
    n.includes("vwfs") ||
    n.includes("vwsf") ||
    n.includes("volkswagen financial") ||
    n.includes("volkswagen bank") ||
    n.includes("volkswagen servicios financieros")
  )
    return "VWSF";
  if (n.includes("seat sf") || n.includes("seat financial")) return "Seat SF";
  if (n.includes("cupra sf") || n.includes("cupra financial")) return "Cupra SF";
  if (n.includes("mstar")) return "Mstar";
  if (n.includes("credito go")) return "Credito GO";
  // FinanMadrid cambió de nombre a Finsus
  if (n.includes("finanmadrid") || n.includes("finan madrid") || n.includes("finsus")) return "Finsus";
  if (n.includes("tipauto") || n.includes("tip auto")) return "TipAuto";

  // Coincidencia exacta con la lista del select.
  const match = FINANCIERA_OPTIONS.find((f) => normalizarTexto(f) === n);

  // Si no está en la lista, se deja vacío para que se elija manualmente.
  return match || "";
};

const detectarFormatoCotizacion = (texto = "") => {
  const n = normalizarTexto(texto);

  if (n.includes("simulador de credito de auto banorte")) return "BANORTE";
  if (n.includes("grupo financiero bbva") || n.includes("seguros bbva mx")) return "BBVA";
  if (n.includes("autoexpress inbursa") && n.includes("chirey")) return "CHIREY_INBURSA";
  if (n.includes("hey banco") || n.includes("oferta hey 2026")) return "HEY_BANCO";
  if (n.includes("condiciones del plan de pagos") || n.includes("kunacapital")) return "KUNA";
  if (n.includes("revisa tus opciones y elige el plan") || n.includes("nexu.mx")) return "NEXU";
  if (n.includes("seminuevos preferente") || n.includes("nr seminuevos")) return "NRFM_SEMINUEVOS";
  if (n.includes("credi nissan") || n.includes("credito nissan") || n.includes("nr finance mexico")) return "NRFM";

  return "GENERICO";
};

const extraerMensualidadRepetida = (texto = "") => {
  const montos = [];
  const regex = /\$\s*([0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]{2}))/g;
  let match;

  while ((match = regex.exec(texto)) !== null) {
    const value = cleanNumberInput(match[1]);
    const num = toNumber(value);
    if (num >= 500 && num <= 100000) montos.push(value);
  }

  const counts = montos.reduce((acc, value) => {
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts)
    .filter(([, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1])[0]?.[0] || "";
};

const esLineaEtiquetaDeFormulario = (linea = "") => {
  const t = String(linea || "").trim();
  if (!t || t.length > 40) return false;
  if (!/:$/.test(t)) return false;
  if (/\$|\d{3,}/.test(t)) return false;
  return true;
};

// Muchas cotizaciones de fintechs (Kuna, y potencialmente otras futuras) se
// aplanan por el OCR agrupando TODAS las etiquetas de un formulario primero
// y TODOS sus valores justo después, en el mismo orden
// ("Nombre del cliente:\nEmail:\nAsesor:\nJUAN PEREZ\ncorreo@x.com\nAna Lopez").
// Un regex de "etiqueta seguida de valor" no puede resolver esto porque la
// etiqueta y su valor están varias líneas separados. Aquí buscamos el primer
// bloque de 3+ líneas-etiqueta consecutivas seguido de un bloque del mismo
// tamaño de líneas-valor, y emparejamos por posición.
const extraerBloqueEtiquetaValor = (texto = "", definiciones = []) => {
  const lineas = String(texto || "")
    .split("\n")
    .map((linea) => linea.trim());
  const resultado = {};

  for (let inicio = 0; inicio < lineas.length; inicio += 1) {
    if (!esLineaEtiquetaDeFormulario(lineas[inicio])) continue;

    let fin = inicio;
    while (fin + 1 < lineas.length && esLineaEtiquetaDeFormulario(lineas[fin + 1])) {
      fin += 1;
    }

    const etiquetasBloque = lineas.slice(inicio, fin + 1);

    if (etiquetasBloque.length < 3) {
      inicio = fin;
      continue;
    }

    const valoresBloque = lineas.slice(fin + 1, fin + 1 + etiquetasBloque.length);
    const bloqueValido =
      valoresBloque.length === etiquetasBloque.length &&
      valoresBloque.every((valor) => valor && !esLineaEtiquetaDeFormulario(valor));

    if (bloqueValido) {
      definiciones.forEach(({ campo, etiquetas }) => {
        if (resultado[campo]) return;
        const idx = etiquetasBloque.findIndex((etiqueta) =>
          etiquetas.some((regex) => regex.test(etiqueta)),
        );
        if (idx >= 0 && valoresBloque[idx]) {
          resultado[campo] = limpiarValorOCR(valoresBloque[idx]);
        }
      });
    }

    inicio = fin;
  }

  return resultado;
};

const extraerDatosCotizacionPorReglas = (textoOriginal = "") => {
  const texto = String(textoOriginal || "").replace(/\r/g, "");
  const formato = detectarFormatoCotizacion(texto);
  const datos = { FormatoDetectado: formato };

  if (formato === "BANORTE") {
    datos.Financiera = "Banorte";
    datos.PrecioVehiculo = firstRegexValue(texto, [/Valor Factura:\s*\$?\s*([\d,.]+)/i]);
    datos.Enganche = firstRegexValue(texto, [/Enganche:\s*\$?\s*([\d,.]+)/i]);
    datos.TasaDeInteres = firstRegexValue(texto, [/Tasa ordinaria anual:\s*([\d.]+)\s*%/i]);
    datos.PlazoMeses = firstRegexValue(texto, [/Periodos:\s*(\d+)/i]);
    if (!datos.PlazoMeses) {
      const years = toNumber(firstRegexValue(texto, [/Plazo en años:\s*(\d+)/i]));
      if (years) datos.PlazoMeses = String(years * 12);
    }
    datos.MontoComisionApertura = firstRegexValue(texto, [/Comisi[oó]n contrataci[oó]n cr[eé]dito:\s*\$?\s*([\d,.]+)/i]);
    datos.MontoSeguro = firstRegexValue(texto, [/Monto por seguro de daños Contado:\s*\$?\s*([\d,.]+)/i]);
    datos.TipoCompraSeguro = /seguro de daños contado/i.test(texto) ? "Contado" : "";
    datos.Mensualidad = extraerMensualidadRepetida(texto);
    const desc = firstRegexValue(texto, [/Descripci[oó]n del producto:[ \t]*([^\n]+)/i]);
    if (desc) {
      const parts = desc.split("|").map((v) => limpiarValorOCR(v)).filter(Boolean);
      if (parts[0]) {
        const words = parts[0].split(/\s+/);
        if (words.length > 1) {
          datos.Marca = words.shift();
          datos.Modelo = words.join(" ");
        }
      }
      if (parts[1]) datos.Version = parts[1];
    }
    datos.Año = firstRegexValue(texto, [/Descripci[oó]n del producto:[\s\S]{0,160}?\b(20\d{2})\b/i]);
    datos.TipoDeVehiculo = "Nuevo";
  }

  if (formato === "BBVA") {
    datos.Financiera = "BBVA";
    datos.Cliente = firstRegexValue(texto, [/Nombre del cliente:[ \t]*([^\n]*)/i]);
    datos.Telefono = firstRegexValue(texto, [/Tel[eé]fono[ \t]*:?[ \t]*([^\n]*)/i]);
    if (/tasa fija|beneficios|requisitos/i.test(datos.Cliente || "")) delete datos.Cliente;
    if (/plazos|seguro|beneficios|requisitos/i.test(datos.Telefono || "")) delete datos.Telefono;
    datos.Marca = firstRegexValue(texto, [/Marca\s+([^\s\n]+)\s+Submarca/i]);
    datos.Modelo = firstRegexValue(texto, [/Submarca\s+([^\n]+)/i]);
    datos.Version = firstRegexValue(texto, [/Versi[oó]n\s+([^\n]+)/i]);
    datos.Año = firstRegexValue(texto, [/Tipo de veh[ií]culo\s+\S+\s+Modelo\s+(20\d{2})/i]);
    datos.TipoDeVehiculo = firstRegexValue(texto, [/Tipo de veh[ií]culo\s+([^\s\n]+)/i]);
    datos.PrecioVehiculo = firstRegexValue(texto, [/Precio de veh[ií]culo\s*\$?\s*([\d,.]+)/i]);
    datos.Enganche = firstRegexValue(texto, [/Enganche\s+([\d,.]+)\s+\d+(?:\.\d+)?\s*%/i]);
    datos.PlazoMeses = firstRegexValue(texto, [/Plazo\s+(\d+)\s+MESES/i]);
    datos.TasaDeInteres = firstRegexValue(texto, [/Tasa fija anual sin IVA\s+Auto:\s*([\d.]+)\s*%/i]);
    datos.Mensualidad = firstRegexValue(texto, [/Pago Mensual con IVA\s*\$?\s*([\d,.]+)/i]);
    datos.Aseguradora = firstRegexValue(texto, [/Aseguradora[ \t]+(.+?)(?=[ \t]{2,}|\n|$)/i]);
    datos.MontoSeguro = firstRegexValue(texto, [/Importe de seguro\s*\$?\s*([\d,.]+)/i]);
    datos.TipoCompraSeguro = firstRegexValue(texto, [/Tipo de seguro de daños\s+([^\n]+)/i]);
    datos.MontoComisionApertura = firstRegexValue(texto, [/Comisi[oó]n por apertura\s*\$?\s*([\d,.]+)/i]);
    datos.PlanDeFinanciamiento = firstRegexValue(texto, [/Plan\s+([^\n]+)/i]);
  }

  if (formato === "CHIREY_INBURSA") {
    datos.Financiera = "Inbursa";
    datos.Cliente = firstRegexValue(texto, [/Solicitante y\/o acreditado:\s*([^\n]+?)(?=\s+No\. de contrato)/i]);
    datos.Marca = firstRegexValue(texto, [/Marca:\s*([^\n]+?)(?=\s+Tipo:)/i]);
    datos.Modelo = firstRegexValue(texto, [/Veh[ií]culo:\s*([^\n]+?)(?=\s+Tipo resto del plazo:)/i]);
    datos.Año = firstRegexValue(texto, [/Modelo:\s*(20\d{2})/i]);
    datos.TipoDeVehiculo = "Nuevo";
    datos.PrecioVehiculo = firstRegexValue(texto, [/Precio de venta \(con IVA\):\s*\$?\s*([\d,.]+)/i]);
    datos.Enganche = firstRegexValue(texto, [/Enganche\*?:\s*\d+(?:\.\d+)?%\s*\$?\s*([\d,.]+)/i]);
    datos.PlazoMeses = firstRegexValue(texto, [/Plazo:\s*(\d+)\s*meses/i]);
    datos.TasaDeInteres = firstRegexValue(texto, [/Tasa de Inter[eé]s \(en t[eé]rminos anuales simples\):\s*([\d.]+)%/i]);
    datos.Mensualidad = firstRegexValue(texto, [/Mensualidad con seguro primer año \(meses 1-12\):\s*\$?\s*([\d,.]+)/i]);
    datos.Aseguradora = firstRegexValue(texto, [/Aseguradora:[ \t]*(.+?)(?=[ \t]{2,}Accesorios|\n|$)/i]);
    datos.MontoSeguro = firstRegexValue(texto, [/Importe primer año:\s*\$?\s*([\d,.]+)/i, /Seguro primer año:\s*\$?\s*([\d,.]+)/i]);
    datos.TipoCompraSeguro = firstRegexValue(texto, [/Tipo:\s*(Seguro\s+Contado\s+Anual)/i]);
    datos.MontoComisionApertura = firstRegexValue(texto, [/Comisi[oó]n por contrataci[oó]n del cr[eé]dito \(con IVA\):\s*\d+(?:\.\d+)?%\s*\$?\s*([\d,.]+)/i]);
    datos.TipoComision = "Contado";
    datos.PlanDeFinanciamiento = firstRegexValue(texto, [/Plan de financiamiento:\s*([^\n]+)/i]);
  }

  if (formato === "HEY_BANCO") {
    datos.Financiera = "Hey Banco";
    datos.Cliente = firstRegexValue(texto, [/Nombre:\s*([^\n]*?)(?=\s+Tel[eé]fono:|$)/i]);
    datos.Telefono = firstRegexValue(texto, [/Tel[eé]fono:\s*([^\n]*?)(?=\s+Correo:|$)/i]);
    datos.Correo = firstRegexValue(texto, [/Correo:[ \t]*([^\n]*)/i]);
    const vehicleLine = firstRegexValue(texto, [/^(.*?)\s+-\s+AUTOS\s*$/im]);
    if (vehicleLine) {
      const m = vehicleLine.match(/^([A-Z0-9-]+)\s+(20\d{2})\s+([^\s]+)\s+(.+)$/i);
      if (m) {
        datos.Marca = m[1];
        datos.Año = m[2];
        datos.Modelo = m[3];
        datos.Version = m[4];
      }
    }
    datos.TipoDeVehiculo = firstRegexValue(texto, [/Estado del auto[ \t]+(Seminuevo|Nuevo)\b/i]);
    datos.PrecioVehiculo = firstRegexValue(texto, [/Valor del auto\s*\$?\s*([\d,.]+)/i]);
    datos.Enganche = firstRegexValue(texto, [/Enganche\s*\$?\s*([\d,.]+)/i]);
    datos.PlazoMeses = firstRegexValue(texto, [/Plazo\s+(\d+)\s+meses/i]);
    datos.TasaDeInteres = firstRegexValue(texto, [/Tasa anual auto\s+([\d.]+)%/i]);
    datos.Mensualidad = firstRegexValue(texto, [/\b1\s+[^\n]*?\$([\d,.]+)\s*$/m]);
    if (!datos.Mensualidad) datos.Mensualidad = extraerMensualidadRepetida(texto);
    const seguro = texto.match(/Seguro de auto\s+(.+?)\s+\$\s*([\d,.]+)/i);
    if (seguro) {
      datos.Aseguradora = limpiarValorOCR(seguro[1]);
      datos.MontoSeguro = limpiarValorOCR(seguro[2]);
    }
    datos.TipoCompraSeguro = firstRegexValue(texto, [/Tipo de seguro[ \t]+(Contado|Financiado|Mensualidades)\b/i]);
    datos.MontoComisionApertura = firstRegexValue(texto, [/Comisi[oó]n por apertura\s*\$?\s*([\d,.]+)/i]);
  }

  if (formato === "KUNA") {
    datos.Financiera = "Kuna";
    // Kuna aplana su formulario como "Nombre del cliente:\nFecha:\n...\n
    // Agencia:\nJUAN PEREZ\n2026/07/28\n...\nGocar - Nissan-Tecamac", es decir
    // todas las etiquetas primero y luego todos los valores en el mismo
    // orden. "\s*" cruza el salto de línea y captura la SIGUIENTE etiqueta
    // como si fuera el valor (p. ej. Cliente = "Fecha"). Usamos el bloque
    // etiqueta/valor posicional en vez de un regex de una sola línea.
    const bloqueKuna = extraerBloqueEtiquetaValor(texto, [
      { campo: "Cliente", etiquetas: [/^Nombre del cliente:?$/i] },
      { campo: "Correo", etiquetas: [/^Email:?$/i] },
      { campo: "Asesor", etiquetas: [/^Asesor:?$/i] },
      { campo: "Agencia", etiquetas: [/^Agencia:?$/i] },
    ]);
    datos.Cliente = bloqueKuna.Cliente || "";
    datos.Correo = bloqueKuna.Correo || "";
    datos.Asesor = bloqueKuna.Asesor || "";
    datos.Agencia = bloqueKuna.Agencia || "";
    datos.Marca = firstRegexValue(texto, [/Marca auto:[ \t]*(.+?)(?=[ \t]{2,}Aseguradora:|\n|$)/i]);
    datos.Version = firstRegexValue(texto, [/Versi[oó]n:[ \t]*(.+?)(?=[ \t]{2,}Cobertura seguro de daños:|\n|$)/i]);
    datos.Modelo = firstRegexValue(texto, [/Modelo:[ \t]*(.+?)(?=[ \t]{2,}N[uú]mero de P[oó]liza|\n|$)/i]);
    datos.Año = firstRegexValue(texto, [/Año:\s*(20\d{2})/i]);
    datos.PrecioVehiculo = firstRegexValue(texto, [/Precio auto \(con IVA\):\s*\$?\s*([\d,.]+)/i]);
    datos.Enganche = firstRegexValue(texto, [/Enganche:\s*\$?\s*([\d,.]+)/i]);
    datos.PlazoMeses = firstRegexValue(texto, [/Monto en mensualidades:\s*\$?[\d,.]+\s+Plazo:\s*(\d+)\s*meses/i]);
    datos.TasaDeInteres = firstRegexValue(texto, [/Tasa anual sin IVA:\s*([\d.]+)%/i]);
    datos.Aseguradora = firstRegexValue(texto, [/Aseguradora:[ \t]*(.+?)(?=[ \t]{2,}|\n|$)/i]);
    datos.MontoSeguro = firstRegexValue(texto, [/Importe de seguro \(con IVA\):\s*\$?\s*([\d,.]+)/i]);
    datos.TipoCompraSeguro = firstRegexValue(texto, [/Modalidad de pago del seguro:[ \t]*(Mensualidades|Contado|Financiado)\b/i]);
    datos.MontoComisionApertura = firstRegexValue(texto, [/Comisi[oó]n por apertura de[\s\S]{0,80}?\$\s*([\d,.]+)/i]);
    datos.Mensualidad = firstRegexValue(texto, [/\b1\s+20\d{2}-\d{2}-\d{2}[^\n]*?\$\s*([\d,.]+)\s*$/m]);
    if (!datos.Mensualidad) {
      const totals = [...texto.matchAll(/\$\s*([0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]{2}))\s*$/gm)]
        .map((m) => cleanNumberInput(m[1]))
        .filter((v) => toNumber(v) > 1000);
      datos.Mensualidad = totals.find((v) => toNumber(v) > 5000 && toNumber(v) < 50000) || "";
    }
    datos.TipoDeVehiculo = /kilometraje:\s*0\b/i.test(texto) ? "Nuevo" : "";
  }

  if (formato === "NEXU") {
    datos.Financiera = "Nexu";
    datos.Cliente = firstRegexValue(texto, [/Hola\s+([^,\n]+),/i]);
    datos.TipoDeVehiculo = firstRegexValue(texto, [/\b(Seminuevo|Nuevo)\b/i]);
    datos.PrecioVehiculo = firstRegexValue(texto, [/Precio del auto[\s\S]{0,100}?\$\s*([\d,.]+)/i]);

    // El comparativo de plazos (24/36/48/60 meses) se representa en 4
    // columnas que el OCR aplana como líneas separadas. La opción marcada
    // "Recomendado" no siempre es la última columna, así que ubicamos su
    // índice real dentro de los encabezados de plazo y usamos ESE índice
    // (no un número de columna fijo) para leer mensualidad, plazo y capital
    // financiado. "[\s\S]*?" (en vez de "[^\n]*?") es necesario porque cada
    // valor de columna suele ir en su propia línea, no en la misma línea que
    // el encabezado "Pago mensual".
    const encabezadosPlazo = [...texto.matchAll(/(\d+)\s*meses/gi)].map((m) => m[1]);
    let indiceRecomendado = encabezadosPlazo.length - 1; // por defecto, la última columna
    const posRecomendado = texto.search(/Recomendado/i);
    if (posRecomendado >= 0) {
      const posicionesEncabezados = [...texto.matchAll(/(\d+)\s*meses/gi)].map((m) => m.index);
      let idxMasCercano = -1;
      let distMinima = Infinity;
      posicionesEncabezados.forEach((pos, idx) => {
        const dist = Math.abs(pos - posRecomendado);
        if (dist < distMinima) {
          distMinima = dist;
          idxMasCercano = idx;
        }
      });
      if (idxMasCercano >= 0) indiceRecomendado = idxMasCercano;
    }

    const filaPago = texto.match(
      /Pago\s+mensual[\s\S]{0,40}?\$\s*([\d,.]+)[\s\S]{0,60}?\$\s*([\d,.]+)[\s\S]{0,60}?\$\s*([\d,.]+)[\s\S]{0,60}?\$\s*([\d,.]+)/i,
    );
    datos.PlazoMeses = encabezadosPlazo[indiceRecomendado] || (filaPago ? "60" : "");
    datos.Mensualidad =
      filaPago?.[indiceRecomendado + 1] ||
      firstRegexValue(texto, [/Mensualidades desde[\s\S]{0,40}?\$\s*([\d,.]+)/i]);
    datos.TasaDeInteres = firstRegexValue(texto, [/Tasa mensual aplicada:\s*([\d.]+)%/i]);

    // "Enganche" es una ETIQUETA (dentro de "Incluye:") que queda muy lejos
    // de su valor real: el OCR agrupa TODAS las etiquetas de "Pago mensual >
    // Incluye" (Financiamiento, Renta GPS, Seguro de Vida, Accesorios) y
    // "Pago inicial > Incluye" (Enganche, Comisión por apertura, Instalación
    // GPS, Seguro de contado) primero, y luego TODOS sus 8 renglones de
    // montos, en ese mismo orden combinado. Buscar "el primer $ tras la
    // palabra Enganche" atrapaba por error el monto de "Financiamiento".
    // Aquí ubicamos el bloque de 8 renglones de montos que viene justo
    // después de la última etiqueta reconocida y los emparejamos por
    // posición fija.
    const CAMPOS_INCLUYE_NEXU = [
      "Financiamiento",
      "Renta mensual GPS",
      "Seguro de Vida",
      "Accesorios financiados",
      "Enganche",
      "Comisión por apertura",
      "Instalación GPS",
      "Seguro de contado",
    ];
    const posUltimaEtiqueta = Math.max(
      texto.search(/Recomendado/i),
      texto.search(/Seguro\s+de\s+contado/i),
    );
    let filasMontoNexu = [];
    if (posUltimaEtiqueta >= 0) {
      filasMontoNexu = texto
        .slice(posUltimaEtiqueta)
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => /^(\$\s*[\d,.]+\s*)+$/.test(l))
        .slice(0, CAMPOS_INCLUYE_NEXU.length);
    }
    const leerColumnaNexu = (nombreCampo) => {
      const idx = CAMPOS_INCLUYE_NEXU.indexOf(nombreCampo);
      const fila = filasMontoNexu[idx];
      if (!fila) return "";
      const columnas = [...fila.matchAll(/\$\s*([\d,.]+)/g)].map((m) => m[1]);
      return columnas[indiceRecomendado] || columnas[columnas.length - 1] || "";
    };
    datos.Enganche =
      leerColumnaNexu("Enganche") ||
      firstRegexValue(texto, [/Enganche[ \t]*:?[ \t]*\$?[ \t]*([\d,.]+)/i]);
    datos.MontoComisionApertura =
      leerColumnaNexu("Comisión por apertura") ||
      firstRegexValue(texto, [/Comisi[oó]n por apertura[\s\S]{0,120}?\$\s*([\d,.]+)/i]);
    datos.MontoSeguro =
      leerColumnaNexu("Seguro de contado") ||
      firstRegexValue(texto, [/Seguro de contado[\s\S]{0,120}?\$\s*([\d,.]+)/i]);
  }

  if (formato === "NRFM" || formato === "NRFM_SEMINUEVOS") {
    datos.Financiera = "NR Finance";
    datos.Cliente = firstRegexValue(texto, [/Hola\s+([^!\n]+)!/i, /^([A-ZÁÉÍÓÚÑ ]{5,})\s*$/m]);
    datos.Version = firstRegexValue(texto, [/Versi[oó]n:[ \t]*(.+?)(?=[ \t]{2,}|\n|$)/i]);
    datos.Año = firstRegexValue(texto, [/Año:\s*(20\d{2})/i]);
    datos.PrecioVehiculo = firstRegexValue(texto, [/Precio:\s*\$?\s*([\d,.]+)/i]);
    datos.Enganche = firstRegexValue(texto, [/Enganche:\s*\$?\s*([\d,.]+)/i]);
    datos.Mensualidad = firstRegexValue(texto, [/^[ \t]*Mensualidad:[ \t]*\$?[ \t]*([\d,.]+)/im]);
    const plazoPrincipal = texto.match(/Mensualidad:\s*\$?[\d,.]+[\s\S]{0,100}?Plazo:\s*(\d+)\s*meses/i);
    datos.PlazoMeses = plazoPrincipal?.[1] || firstRegexValue(texto, [/Comparativo por plazos:[\s\S]{0,250}?Plazo[^\n]*\b(60)\b/i]);
    datos.TasaDeInteres = firstRegexValue(texto, [/Tasa Anual:\s*([\d.]+)%/i]);
    datos.Aseguradora = firstRegexValue(texto, [/Aseguradora:[ \t]*(.+?)(?=[ \t]+Tasa Anual:|[ \t]{2,}|\n|$)/i]);
    datos.MontoSeguro = firstRegexValue(texto, [/Monto Primer Recibo:\s*\$?\s*([\d,.]+)/i]);
    datos.TipoCompraSeguro = firstRegexValue(texto, [/Tipo:[ \t]*(Gratis\s*\/\s*CONTADO|CONTADO|FINANCIADO)\b/i]);
    datos.MontoComisionApertura = firstRegexValue(texto, [/Comisi[oó]n por Apertura:\s*\$?\s*([\d,.]+)/i]);
    datos.TipoComision = firstRegexValue(texto, [/Tipo Comisi[oó]n por[\s\S]{0,40}?Apertura:\s*([^\n]+)/i]);
    datos.PlanDeFinanciamiento = firstRegexValue(texto, [/Nombre de la Oferta:[ \t]*(.+?)(?=[ \t]{2,}Enganche:|\n|$)/i]);
    if (formato === "NRFM_SEMINUEVOS") datos.TipoDeVehiculo = "Seminuevo";
    else datos.TipoDeVehiculo = "Nuevo";

    const versionN = normalizarTexto(datos.Version);
    if (versionN.includes("march")) {
      datos.Marca = "NISSAN";
      datos.Modelo = "MARCH";
    } else if (versionN.includes("captiva")) {
      datos.Modelo = "CAPTIVA";
    }
  }



  // Fallback universal para cotizaciones no reconocidas.
  // Estas reglas buscan etiquetas comunes sin depender de una financiera específica.
  // OpenAI sigue funcionando después como segunda capa para completar/corregir datos.
  if (formato === "GENERICO") {
    // IMPORTANTE: los campos de texto libre (nombre, correo, financiera, etc.)
    // solo se capturan si el valor está en la MISMA línea que la etiqueta
    // ("[ \t]*" en vez de "\s*"). Muchas cotizaciones (Kuna, Nexu, etc.) se
    // aplanan por el OCR como "Etiqueta1:\nEtiqueta2:\n...\nValor1\nValor2",
    // es decir, todas las etiquetas primero y luego todos los valores. Si
    // dejamos que "\s*" cruce saltos de línea, el regex termina capturando el
    // texto de la SIGUIENTE etiqueta como si fuera el valor (p. ej. Cliente =
    // "Fecha"). Es preferible devolver "" en esos casos: OpenAI recibe el
    // texto completo y sí puede inferir el valor correcto sin ser sobrescrito
    // después por una regla equivocada (ver combinarDatosCotizacion).
    datos.Cliente = firstRegexValue(texto, [
      /Nombre(?:\s+del)?\s+cliente[ \t]*:?[ \t]*([^\n]+)/i,
      /Solicitante(?:\s+y\/o\s+acreditado)?[ \t]*:?[ \t]*([^\n]+)/i,
      /Acreditado[ \t]*:?[ \t]*([^\n]+)/i,
    ]);

    datos.Telefono = firstRegexValue(texto, [
      /\bTel[eé]fono[ \t]*:?[ \t]*([^\n]+)/i,
      /\bCelular[ \t]*:?[ \t]*([^\n]+)/i,
    ]);

    datos.Correo = firstRegexValue(texto, [
      /\bCorreo(?:\s+electr[oó]nico)?[ \t]*:?[ \t]*([^\n]+)/i,
      /\bE-?mail[ \t]*:?[ \t]*([^\n]+)/i,
    ]);

    datos.PrecioVehiculo = firstRegexValue(texto, [
      /Precio(?:\s+del|\s+de)?\s+(?:veh[ií]culo|auto)(?:\s*\(con IVA\))?\s*:?\s*\$?\s*([\d,.]+)/i,
      /Valor(?:\s+del|\s+de)?\s+(?:veh[ií]culo|auto)(?:\s*\(con IVA\))?\s*:?\s*\$?\s*([\d,.]+)/i,
      /Precio\s+de\s+venta(?:\s*\(con IVA\))?\s*:?\s*\$?\s*([\d,.]+)/i,
      /Valor\s+factura\s*:?\s*\$?\s*([\d,.]+)/i,
    ]);

    datos.Enganche = firstRegexValue(texto, [
      /Enganche(?:\s*\*)?\s*:?\s*(?:\d+(?:\.\d+)?\s*%\s*)?\$?\s*([\d,.]+)/i,
      /Enganche(?:\s*\*)?\s*:?\s*\$?\s*([\d,.]+)\s*(?:\d+(?:\.\d+)?\s*%)?/i,
    ]);

    datos.PlazoMeses = firstRegexValue(texto, [
      /Plazo(?:\s+del\s+cr[eé]dito)?\s*:?\s*(\d+)\s*(?:meses|mensualidades)\b/i,
      /Periodos?\s*:?\s*(\d+)\b/i,
    ]);

    datos.TasaDeInteres = firstRegexValue(texto, [
      /Tasa(?:\s+de)?\s+inter[eé]s(?:\s+anual)?(?:\s+sin\s+IVA)?\s*:?\s*([\d.]+)\s*%/i,
      /Tasa\s+anual(?:\s+auto)?(?:\s+sin\s+IVA)?\s*:?\s*([\d.]+)\s*%/i,
      /Tasa\s+ordinaria\s+anual\s*:?\s*([\d.]+)\s*%/i,
    ]);

    datos.Mensualidad = firstRegexValue(texto, [
      /Pago\s+mensual(?:\s+con\s+IVA)?\s*:?\s*\$?\s*([\d,.]+)/i,
      /Mensualidad(?:\s+estimada)?\s*:?\s*\$?\s*([\d,.]+)/i,
      /Mensualidades?\s+desde\s*:?\s*\$?\s*([\d,.]+)/i,
    ]);

    datos.Financiera = firstRegexValue(texto, [
      /\bFinanciera[ \t]*:?[ \t]*([^\n]+)/i,
      /\bBanco[ \t]*:?[ \t]*([^\n]+)/i,
      /Instituci[oó]n\s+financiera[ \t]*:?[ \t]*([^\n]+)/i,
    ]);

    datos.Aseguradora = firstRegexValue(texto, [
      /\bAseguradora[ \t]*:?[ \t]*([^\n]+)/i,
      /Compa[nñ][ií]a\s+de\s+seguros[ \t]*:?[ \t]*([^\n]+)/i,
    ]);

    datos.MontoSeguro = firstRegexValue(texto, [
      /(?:Importe|Monto)(?:\s+del)?\s+seguro(?:\s+de\s+da[nñ]os|\s+de\s+auto)?\s*:?\s*\$?\s*([\d,.]+)/i,
      /Seguro(?:\s+de)?\s+auto[^\n$]*\$\s*([\d,.]+)/i,
      /Seguro\s+primer\s+a[nñ]o\s*:?\s*\$?\s*([\d,.]+)/i,
    ]);

    datos.TipoCompraSeguro = firstRegexValue(texto, [
      /Tipo\s+de\s+seguro(?:\s+de\s+da[nñ]os)?\s*:?\s*(Contado|Financiado|Mensualidades|Anual\s+Contado)\b/i,
      /Modalidad\s+de\s+pago(?:\s+del\s+seguro)?\s*:?\s*(Contado|Financiado|Mensualidades)\b/i,
    ]);

    datos.MontoComisionApertura = firstRegexValue(texto, [
      /Comisi[oó]n(?:\s+por|\s+de)?\s+apertura(?:\s+de\s+cr[eé]dito)?(?:\s*\(con IVA\))?\s*:?\s*\$?\s*([\d,.]+)/i,
      /Comisi[oó]n\s+contrataci[oó]n\s+cr[eé]dito\s*:?\s*\$?\s*([\d,.]+)/i,
    ]);

    datos.PlanDeFinanciamiento = firstRegexValue(texto, [
      /Plan(?:\s+de)?\s+financiamiento[ \t]*:?[ \t]*([^\n]+)/i,
      /Nombre\s+de\s+la\s+oferta[ \t]*:?[ \t]*([^\n]+)/i,
      /\bProducto[ \t]*:?[ \t]*([^\n]+)/i,
    ]);

    datos.Marca = firstRegexValue(texto, [
      /\bMarca(?:\s+auto)?[ \t]*:?[ \t]*([^\n]+)/i,
    ]);

    datos.Modelo = firstRegexValue(texto, [
      /\bSubmarca[ \t]*:?[ \t]*([^\n]+)/i,
      /\bModelo(?:\s+del\s+veh[ií]culo)?[ \t]*:?[ \t]*([^\n]+)/i,
    ]);

    // "\b" evita que "Versión" haga match dentro de "Inversión" (Nexu la usa
    // como etiqueta de pago inicial) y capture basura como "inicial".
    datos.Version = firstRegexValue(texto, [
      /\bVersi[oó]n[ \t]*:?[ \t]*([^\n]+)/i,
    ]);

    datos.Año = firstRegexValue(texto, [
      /\bA[nñ]o[ \t]*:?[ \t]*(20\d{2})\b/i,
      /\bModelo[ \t]*:?[ \t]*(20\d{2})\b/i,
    ]);

    datos.TipoDeVehiculo = firstRegexValue(texto, [
      /(?:Tipo|Estado)(?:\s+del)?\s+(?:veh[ií]culo|auto)[ \t]*:?[ \t]*(Nuevo|Seminuevo)\b/i,
      /Condici[oó]n(?:\s+del)?\s+auto[ \t]*:?[ \t]*(Nuevo|Seminuevo)\b/i,
    ]);

    datos.Agencia = firstRegexValue(texto, [
      /\bAgencia[ \t]*:?[ \t]*([^\n]+)/i,
      /\bDistribuidora?[ \t]*:?[ \t]*([^\n]+)/i,
    ]);

    datos.Asesor = firstRegexValue(texto, [
      /\bAsesor[ \t]*:?[ \t]*([^\n]+)/i,
      /\bVendedor[ \t]*:?[ \t]*([^\n]+)/i,
    ]);

    // Respaldo para PDFs de 2 columnas donde el OCR agrupa TODAS las
    // etiquetas primero y luego TODOS los valores (p. ej. Kuna: "Nombre del
    // cliente:\nFecha:\n...\nAgencia:\nJESUS PEREZ\n2026/07/28\n...").
    // Si el bloque anterior no encontró Cliente/Correo/Agencia/Asesor,
    // buscamos ese patrón de bloque y emparejamos etiqueta con valor por
    // posición dentro del bloque.
    const bloque = extraerBloqueEtiquetaValor(texto, [
      { campo: "Cliente", etiquetas: [/^Nombre del cliente:?$/i] },
      { campo: "Correo", etiquetas: [/^Email:?$/i, /^Correo(?:\s+electr[oó]nico)?:?$/i] },
      { campo: "Asesor", etiquetas: [/^Asesor:?$/i, /^Vendedor:?$/i] },
      { campo: "Agencia", etiquetas: [/^Agencia:?$/i, /^Distribuidora?:?$/i] },
    ]);
    Object.entries(bloque).forEach(([campo, valor]) => {
      if (!datos[campo] && valor) datos[campo] = valor;
    });
  }

  return Object.fromEntries(
    Object.entries(datos).filter(([, value]) => value !== "" && value !== null && value !== undefined),
  );
};

const construirPromptCotizacion = (texto = "", datosReglas = {}) => `
Analiza una cotización automotriz mexicana y devuelve SOLAMENTE JSON válido, sin markdown.
No inventes datos. Si un dato no aparece de forma explícita, usa cadena vacía.

Devuelve exactamente este formato:
{
  "datos": {
    "Marca": "", "Año": "", "Modelo": "", "Version": "",
    "Cliente": "", "Nombre": "", "ApellidoPaterno": "", "ApellidoMaterno": "", "Telefono": "", "Correo": "",
    "PrecioVehiculo": "", "PrecioTotal": "", "MontoFinanciar": "",
    "PlazoMeses": "", "Enganche": "", "Mensualidad": "",
    "Banco": "", "Financiera": "", "TipoDeVehiculo": "",
    "PlanDeFinanciamiento": "", "TasaDeInteres": "",
    "Agencia": "", "Distribuidor": "", "Asesor": "",
    "MetodoContacto": "", "OrigenCliente": "", "Fuente": "", "FuenteOrigen": "",
    "Aseguradora": "", "MontoSeguro": "", "TipoCompraSeguro": "",
    "MontoComisionApertura": "", "TipoComision": "",
    "IncluyePlacas": "", "MontoPlacas": ""
  }
}

REGLAS IMPORTANTES:
1. PrecioVehiculo = precio/valor del automóvil, NO monto total a financiar.
2. Enganche = enganche, NO pago inicial/desembolso/inversión inicial.
3. PlazoMeses = plazo PRINCIPAL del crédito. Ignora plazo del seguro.
4. TasaDeInteres = tasa del AUTO/crédito sin IVA o tasa anual simple. No uses CAT. Si el documento solo muestra tasa mensual, conserva esa tasa literal.
5. Mensualidad = mensualidad PRINCIPAL seleccionada/cotizada. No uses GPS, seguro, pago irregular ni una mensualidad de una tabla cuando existe una mensualidad resumen.
6. En NR Finance/NRFM, usa la Mensualidad y Plazo del encabezado/resumen del plan, no el plazo del seguro ni el comparativo.
7. En Chirey/Inbursa, usa como mensualidad la de meses 1-12 y como tasa la tasa anual simple, no la tasa con IVA.
8. En BBVA, usa Pago Mensual con IVA del resumen.
9. En Banorte, si el primer pago es irregular y luego se repite un pago mensual, usa el pago recurrente.
10. En Nexu, si hay varias columnas, usa la opción marcada Recomendada; Enganche es el renglón Enganche y no Pago inicial.
11. MontoSeguro = seguro de daños/auto. No sumes vida/desempleo salvo que el PDF solo dé un total sin desglose.
12. Banco/Financiera debe ser el otorgante del crédito: Banorte, BBVA, Inbursa, Hey Banco, Kuna, Nexu o NR Finance según corresponda.
13. No deduzcas teléfono, correo, método de contacto, origen, placas o agencia si no están escritos.
14. Para el cliente separa el nombre en tres campos: Nombre = nombre(s), ApellidoPaterno = primer apellido y ApellidoMaterno = segundo apellido. Si el PDF solo muestra el nombre completo en Cliente, úsalo como referencia para separar esos tres campos. No pongas el nombre completo dentro de Nombre.
15. Si FormatoDetectado es GENERICO, analiza la estructura completa de la cotización y usa las etiquetas del documento para identificar los campos. Las reglas preliminares son solo una pista: si una regla genérica capturó texto adicional o un concepto incorrecto, corrígelo usando el contexto del PDF.

Extracción determinística preliminar (úsala como pista y corrígela solo si el texto demuestra que está mal):
${JSON.stringify(datosReglas)}

TEXTO OCR:
${texto}
`;

const combinarDatosCotizacion = (datosIA = {}, datosReglas = {}) => {
  const resultado = { ...datosIA };
  const camposPreferirReglas = [
    "Financiera",
    "PrecioVehiculo",
    "Enganche",
    "PlazoMeses",
    "TasaDeInteres",
    "Mensualidad",
    "Aseguradora",
    "MontoSeguro",
    "TipoCompraSeguro",
    "MontoComisionApertura",
    "TipoComision",
    "TipoDeVehiculo",
  ];

  camposPreferirReglas.forEach((campo) => {
    if (datosReglas?.[campo] !== undefined && datosReglas?.[campo] !== "") {
      resultado[campo] = datosReglas[campo];
    }
  });

  ["Cliente", "Nombre", "ApellidoPaterno", "ApellidoMaterno", "Telefono", "Correo", "Marca", "Modelo", "Año", "Version", "Agencia", "Distribuidor", "Asesor", "PlanDeFinanciamiento"].forEach((campo) => {
    if (!resultado?.[campo] && datosReglas?.[campo]) resultado[campo] = datosReglas[campo];
  });

  resultado.Financiera = normalizarFinanciera(resultado.Financiera || resultado.Banco || "");
  return resultado;
};

const getValue = (obj, keys = []) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== "") {
      return obj[key];
    }
  }

  return "";
};

const estaDisponible = (item) =>
  item?.available === true ||
  item?.available === "true" ||
  item?.available === 1 ||
  item?.available === "1" ||
  item?.disponible === true ||
  item?.disponible === "true" ||
  item?.active === true ||
  item?.active === "true" ||
  item?.activo === 1 ||
  item?.activo === "1";

const coincideAgenciaItem = (item, agIdActivo = "") => {
  const itemAgId = String(item?.ag_id || "")
    .trim()
    .toLowerCase();
  const agId = String(agIdActivo || "")
    .trim()
    .toLowerCase();

  if (!itemAgId || itemAgId === "all") return true;
  if (!agId) return false;

  return itemAgId === agId;
};

const coincideMarcaItem = (item, marcaActiva = "") => {
  const itemBrand = normalizarTexto(item?.brand || item?.marca || "");
  const marca = normalizarTexto(marcaActiva || "");

  if (!itemBrand || itemBrand === "all") return true;
  if (!marca) return false;

  return itemBrand === marca;
};

const getAccessoryPrice = (item) =>
  toNumber(
    item?.price_promo ??
      item?.precio_promo ??
      item?.price ??
      item?.precio ??
      item?.price_base ??
      item?.precio_base ??
      0,
  );

const normalizeAccessoryForSave = (item) => {
  const price = getAccessoryPrice(item);

  return {
    id: item?.id || item?.ag_id || item?.title || "",
    title: item?.title || item?.titulo || "",
    description: item?.description || item?.descripcion || "",
    price,
    price_promo: toNumber(item?.price_promo ?? item?.precio_promo ?? price),
    price_base: toNumber(item?.price_base ?? item?.precio_base ?? price),
    type: item?.type || item?.tipo || "accesorios",
    image: item?.image || item?.img || item?.url_img || "",
    ag_id: item?.ag_id || "",
    brand: item?.brand || item?.marca || "",
  };
};

function MoneyInput({ id, value, onChange, disabled, placeholder = "0" }) {
  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      className="form-control"
      placeholder={placeholder}
      value={formatThousands(value)}
      onChange={(e) => onChange(cleanNumberInput(e.target.value))}
      disabled={disabled}
    />
  );
}

function CreateRequest() {
  const modules = JSON.parse(localStorage.getItem("modulos")) || [];
  const location = useLocation();
  const inventarioRequestIdRef = useRef(0);
  const inventarioCacheRef = useRef({});
  const fileInputRef = useRef(null);

  const tabs = [
    { name: "Crear", to: "/deals/request", module: "deals-request" },
    { name: "Solicitudes", to: "/deals/update", module: "deals-update" },
    { name: "Resumen", to: "/deals/requests", module: "deals-requests" },
    { name: "Ranking", to: "/deals/ranking", module: "deals-ranking" },
  ];

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [agencias, setAgencias] = useState([]);
  const [selectedAgencia, setSelectedAgencia] = useState("");
  const [asesores, setAsesores] = useState([]);
  const [cargandoAsesores, setCargandoAsesores] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [tableData, setTableData] = useState({ food: [] });
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [inventoryError, setInventoryError] = useState("");
  const [selectedAccessoryIds, setSelectedAccessoryIds] = useState([]);
  const [busquedaAccesorio, setBusquedaAccesorio] = useState("");

  const [cliente, setCliente] = useState(INITIAL_CLIENTE);
  const [selectedVehicle, setSelectedVehicle] = useState(
    INITIAL_SELECTED_VEHICLE,
  );
  const [financiamiento, setFinanciamiento] = useState(INITIAL_FINANCIAMIENTO);

  const [condicionVehiculo, setCondicionVehiculo] = useState("nuevos");
  // depende_auto: No = Crédito, Si = Contado, Arrendamiento = Arrendamiento.
  const [dependeAuto, setDependeAuto] = useState("No");

  // Solicitud tardía (solo visible el día de habilitación)
  const [solicitudTardia, setSolicitudTardia] = useState(false);
  const [fechaTardia, setFechaTardia] = useState("");
  const diaTardiaHabilitado = esDiaSolicitudTardia();
  const rangoTardia = getRangoMesAnterior();

  const tipoAutoNormalizado = useMemo(() => {
    const value = normalizarTexto(condicionVehiculo);
    return value.includes("semi") ? "seminuevos" : "nuevos";
  }, [condicionVehiculo]);

  const selectedAgency = useMemo(() => {
    return agencias.find(
      (agency) => String(agency.ag_id) === String(selectedAgencia),
    );
  }, [agencias, selectedAgencia]);

  const selectedAgencyName =
    selectedAgency?.nombre || selectedVehicle.agency || "";

  const getAsesorId = (asesor) => {
    return (
      asesor?.id ||
      asesor?.user_id ||
      asesor?.usuario_id ||
      asesor?.id_usuario ||
      asesor?.uid ||
      ""
    );
  };

  const getAsesorNombre = (asesor) => {
    return (
      asesor?.nombre ||
      asesor?.name ||
      asesor?.asesor ||
      asesor?.usuario ||
      asesor?.nombre_usuario ||
      asesor?.correo ||
      ""
    );
  };

  const getAsesorNombreDisplay = (asesor) =>
    formatName(cleanName(getAsesorNombre(asesor)));

  const asesoresOrdenados = [...asesores].sort((a, b) => {
    const an = cleanName(getAsesorNombre(a));
    const bn = cleanName(getAsesorNombre(b));
    return an.localeCompare(bn, "es", { sensitivity: "base" });
  });

  const selectedAsesor = useMemo(() => {
    return asesores.find(
      (asesor) => String(getAsesorId(asesor)) === String(financiamiento.userId),
    );
  }, [asesores, financiamiento.userId]);

  const asesorNombre = selectedAsesor
    ? getAsesorNombreDisplay(selectedAsesor)
    : financiamiento.asesor || "";

  const precioVehiculoBase = useMemo(
    () => toNumber(financiamiento.precioVehiculo),
    [financiamiento.precioVehiculo],
  );

  const filteredAccessoriesAndWarranties = useMemo(() => {
    const tipoAuto = normalizarTexto(tipoAutoNormalizado || "");
    const marcaActual = normalizarTexto(selectedVehicle.brand || "");
    const agIdActual = selectedAgencia || "";

    const soloParaNuevos = [
      "Garantía Extendida",
      "Cobertura de Llaves",
      "Canastilla de techo",
      "Rejilla Separadora",
      "Tapete uso rudo area de carga",
      "Organizador de Cajuela",
      "Seguro LLantas",
    ].map(normalizarTexto);

    const soloParaSeminuevos = ["Kit de seguridad", "Localizador Gocar"].map(
      normalizarTexto,
    );

    const esNuevo = tipoAuto === "nuevos" || tipoAuto === "nuevo";
    const esSeminuevo = tipoAuto === "seminuevos" || tipoAuto === "seminuevo";

    if (!Array.isArray(tableData?.food)) return [];

    // Filtro de modelo/versión (XTRAIL -> X-Trail, KICKSPLAY -> Kicks Play, Frontier + LE...)
    const coincideModeloVersion = crearFiltroModeloAccesorios(
      [
        ...tableData.food.filter(
          (item) =>
            item &&
            coincideMarcaItem(item, marcaActual) &&
            coincideAgenciaItem(item, agIdActual),
        ),
        ...(Array.isArray(tableData?.modelosNuevos)
          ? tableData.modelosNuevos.map((description) => ({
              type: "nuevos",
              description,
            }))
          : []),
      ],
      {
        marca: selectedVehicle.brand || "",
        modelo: selectedVehicle.model || "",
        version: selectedVehicle.version || "",
      },
    );

    return tableData.food
      .filter((item) => {
        if (!item || item.hide) return false;
        if (!estaDisponible(item)) return false;
        if (!coincideMarcaItem(item, marcaActual)) return false;
        if (!coincideAgenciaItem(item, agIdActual)) return false;

        const itemType = normalizarTexto(item.type || "");
        const esGarantia = itemType === "garantias";
        const esAccesorio = itemType === "accesorios";
        const tituloNorm = normalizarTexto(item.title || "");

        if (!esGarantia && !esAccesorio) return false;

        const esProteccion5000 =
          esGarantia &&
          tituloNorm.includes("proteccion de llanta rin") &&
          tituloNorm.includes("5,000");

        const esProteccion12000 =
          esGarantia &&
          tituloNorm.includes("proteccion de llanta rin") &&
          tituloNorm.includes("12,000");

        if (esSeminuevo && (esProteccion5000 || esProteccion12000)) {
          return false;
        }

        const esGarantiaMecanica =
          tituloNorm.includes("garantia mecanica") ||
          tituloNorm.includes("mecanica amplia");

        if (esNuevo && esGarantiaMecanica) return false;

        const esSoloSeminuevos = soloParaSeminuevos.some((palabra) =>
          tituloNorm.includes(palabra),
        );

        if (esNuevo && esSoloSeminuevos) return false;

        const esSoloParaNuevos = soloParaNuevos.some((palabra) =>
          tituloNorm.includes(palabra),
        );

        if (esSeminuevo && esSoloParaNuevos) return false;

        if (!coincideModeloVersion(item)) return false;

        return true;
      })
      .map((item, index) => {
        let finalTitle = item.title || "";

        if (finalTitle.includes(" - ")) {
          finalTitle = finalTitle.split(" - ")[0];
        }

        return {
          ...item,
          title: finalTitle,
          __manual_id: String(item.id || `${finalTitle}-${item.type}-${index}`),
        };
      });
  }, [
    tableData?.food,
    tipoAutoNormalizado,
    selectedVehicle.model,
    selectedVehicle.version,
    selectedVehicle.brand,
    selectedAgencia,
  ]);

  const accesoriosGarantiasVisibles = useMemo(() => {
    const query = normalizarTexto(busquedaAccesorio || "");

    if (!query) return filteredAccessoriesAndWarranties;

    // Si el asesor escribe exactamente el nombre de un accesorio/garantía,
    // mostramos únicamente las coincidencias exactas para evitar resultados de más.
    const coincidenciasExactas = filteredAccessoriesAndWarranties.filter(
      (item) =>
        normalizarTexto(item?.title || item?.titulo || "") === query,
    );

    if (coincidenciasExactas.length) {
      return coincidenciasExactas;
    }

    // Si no existe coincidencia exacta, primero buscamos coincidencias parciales
    // únicamente sobre el nombre del accesorio/garantía.
    const coincidenciasParciales = filteredAccessoriesAndWarranties.filter(
      (item) =>
        normalizarTexto(item?.title || item?.titulo || "").includes(query),
    );

    if (coincidenciasParciales.length) {
      return coincidenciasParciales;
    }

    // Como último respaldo permitimos una pequeña tolerancia a errores de escritura,
    // pero siempre comparando contra el nombre, no contra la descripción.
    return filteredAccessoriesAndWarranties.filter((item) =>
      coincideBusquedaAproximada(
        item?.title || item?.titulo || "",
        query,
      ),
    );
  }, [filteredAccessoriesAndWarranties, busquedaAccesorio]);

  const selectedAccessories = useMemo(() => {
    const selectedSet = new Set(selectedAccessoryIds.map(String));

    return filteredAccessoriesAndWarranties
      .filter((item) => selectedSet.has(String(item.__manual_id)))
      .map(normalizeAccessoryForSave);
  }, [selectedAccessoryIds, filteredAccessoriesAndWarranties]);

  const totalAccesorios = useMemo(() => {
    return selectedAccessories.reduce(
      (total, accessory) => total + toNumber(accessory.price),
      0,
    );
  }, [selectedAccessories]);

  const precioTotalFinal = useMemo(() => {
    return Math.round(precioVehiculoBase + totalAccesorios);
  }, [precioVehiculoBase, totalAccesorios]);

  const mensualidadContado = useMemo(() => {
    if (dependeAuto !== "Si") return 0;

    const meses = Number(financiamiento.plazo || 0);
    if (!meses || ![3, 6, 9].includes(meses)) return 0;

    // Contado: el auto se paga de contado; los MSI son únicamente
    // para accesorios y garantías.
    return totalAccesorios > 0
      ? Math.round(totalAccesorios / meses)
      : 0;
  }, [dependeAuto, financiamiento.plazo, totalAccesorios]);

  const planCliente = useMemo(
    () => ({
      title: "Plan cliente",
      accessories: selectedAccessories,
      totalPrice: totalAccesorios,
      totalPrice_promo: totalAccesorios,
      totalPrice_base: totalAccesorios,
      isSelected: true,
    }),
    [selectedAccessories, totalAccesorios],
  );

  const selectedCarManual = useMemo(
    () => ({
      title: selectedVehicle.brand,
      marca: selectedVehicle.brand,
      description: selectedVehicle.model,
      modelo: selectedVehicle.model,
      year: selectedVehicle.ano,
      ano: selectedVehicle.ano,
      version: selectedVehicle.version,
      price: precioVehiculoBase,
      precio_vehiculo: precioVehiculoBase,
      precio_total: precioTotalFinal,
      type: tipoAutoNormalizado,
      ag_id: selectedAgencia,
      agencia: selectedAgencyName,
      availableOptions: {
        size: selectedVehicle.version
          ? [
              {
                text: selectedVehicle.version,
                price: String(precioVehiculoBase),
              },
            ]
          : [],
      },
    }),
    [
      selectedVehicle,
      precioVehiculoBase,
      precioTotalFinal,
      tipoAutoNormalizado,
      selectedAgencia,
      selectedAgencyName,
    ],
  );

  const orderCartManual = useMemo(() => {
    const vehicleItem = {
      title: selectedVehicle.brand,
      description: selectedVehicle.model,
      year: selectedVehicle.ano,
      version: selectedVehicle.version,
      price: precioVehiculoBase,
      quantity: 1,
      type: tipoAutoNormalizado,
      ag_id: selectedAgencia,
      availableOptions: selectedCarManual.availableOptions,
    };

    const accessoryItems = selectedAccessories.map((item) => ({
      ...item,
      quantity: 1,
    }));

    return [vehicleItem, ...accessoryItems];
  }, [
    selectedVehicle,
    precioVehiculoBase,
    tipoAutoNormalizado,
    selectedAgencia,
    selectedCarManual.availableOptions,
    selectedAccessories,
  ]);

  useEffect(() => {
    const validIds = new Set(
      filteredAccessoriesAndWarranties.map((item) => String(item.__manual_id)),
    );

    setSelectedAccessoryIds((prev) =>
      prev.filter((itemId) => validIds.has(String(itemId))),
    );
  }, [filteredAccessoriesAndWarranties]);

  const cargarAgencias = async () => {
    let agenciasStorage = [];

    try {
      agenciasStorage = JSON.parse(localStorage.getItem("agencias")) || [];
    } catch (error) {
      console.error("No se pudieron leer agencias del localStorage:", error);
      agenciasStorage = [];
    }

    if (!agenciasStorage.length || !ACCESS_TOKEN) {
      console.error("No hay agencias o access token disponibles.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}financiamiento/usuario/agencias`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            access_token: ACCESS_TOKEN,
            agencias: agenciasStorage,
          }),
        },
      );

      const data = await response.json();

      if (response.ok && data.data) {
        setAgencias(data.data);
      } else {
        console.error("Error al cargar las agencias:", data.message);
      }
    } catch (error) {
      console.error("Error en la solicitud de agencias:", error);
    }
  };

  const cargarAsesoresPorAgencia = async (agId) => {
    if (!agId) {
      setAsesores([]);
      return;
    }

    try {
      setCargandoAsesores(true);

      const response = await fetch(`${API_BASE_URL}obtener/asesores/agencia`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_token: ACCESS_TOKEN,
          ag_id: agId,
        }),
      });

      const data = await response.json();

      const listaAsesores =
        data?.statusCode === 200 && Array.isArray(data?.data?.asesores)
          ? data.data.asesores
          : [];

      setAsesores(listaAsesores);
    } catch (error) {
      console.error("Error al cargar asesores:", error);
      setAsesores([]);
    } finally {
      setCargandoAsesores(false);
    }
  };

  // ---- Placas: catálogo de la agencia para sugerir el monto en automático ----
  const [catalogoPlacas, setCatalogoPlacas] = useState([]);
  const montoPlacasAutoRef = useRef("");

  useEffect(() => {
    const agId = String(selectedAgencia || "").trim();
    const marca = String(selectedVehicle.brand || "").trim();

    if (!agId) {
      setCatalogoPlacas([]);
      return undefined;
    }

    const controller = new AbortController();

    fetch(`${API_BASE_URL}financiamiento/placas`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ access_token: ACCESS_TOKEN, ag_id: agId, marca }),
      signal: controller.signal,
    })
      .then((res) => (res.ok ? res.json() : { placas: [] }))
      .then((data) =>
        setCatalogoPlacas(Array.isArray(data?.placas) ? data.placas : []),
      )
      .catch((error) => {
        if (error?.name === "AbortError") return;
        console.error("No se pudo cargar el catálogo de placas:", error);
        setCatalogoPlacas([]);
      });

    return () => controller.abort();
  }, [selectedAgencia, selectedVehicle.brand]);

  const montoPlacasSugerido = useMemo(
    () =>
      obtenerMontoPlacasPorModelo(catalogoPlacas, {
        marca: selectedVehicle.brand || "",
        modelo: selectedVehicle.model || "",
        version: selectedVehicle.version || "",
      }),
    [
      catalogoPlacas,
      selectedVehicle.brand,
      selectedVehicle.model,
      selectedVehicle.version,
    ],
  );

  // Si incluye placas, se llena el monto sugerido. Solo se sobreescribe si el
  // campo está vacío o todavía tiene el último valor automático (si el asesor
  // lo editó a mano, o vino del documento, se respeta).
  useEffect(() => {
    if (financiamiento.incluyePlacas !== "Si" || !montoPlacasSugerido) return;

    setFinanciamiento((prev) => {
      const actual = String(prev.montoPlacas || "");
      if (actual && actual !== montoPlacasAutoRef.current) return prev;
      if (actual === montoPlacasSugerido) return prev;

      montoPlacasAutoRef.current = montoPlacasSugerido;
      return { ...prev, montoPlacas: montoPlacasSugerido };
    });
  }, [financiamiento.incluyePlacas, montoPlacasSugerido]);

  const cargarInventarioAccesorios = useCallback(async () => {
    const agId = String(selectedAgencia || "").trim();
    const marca = String(selectedVehicle.brand || "").trim();

    if (!agId || !marca) {
      setTableData({ food: [] });
      setInventoryError("");
      return;
    }

    const cacheKey = `${agId}__${normalizarTexto(marca)}`;

    if (inventarioCacheRef.current?.[cacheKey]) {
      setTableData(inventarioCacheRef.current[cacheKey]);
    }

    const requestId = ++inventarioRequestIdRef.current;

    try {
      setLoadingInventory(true);
      setInventoryError("");

      const response = await fetch(`${API_BASE_URL}financiamiento/vehiculos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_token: ACCESS_TOKEN,
          ag_id: agId,
          marca,
        }),
      });

      const data = await response.json();

      if (requestId !== inventarioRequestIdRef.current) return;

      const foodOriginal = Array.isArray(data?.food) ? data.food : [];

      const foodFiltrado = foodOriginal.filter((item) => {
        const itemType = normalizarTexto(item?.type || "");

        if (itemType === "accesorios" || itemType === "garantias") {
          return (
            !item?.hide &&
            estaDisponible(item) &&
            coincideMarcaItem(item, marca) &&
            coincideAgenciaItem(item, agId)
          );
        }

        return false;
      });

      const dataLimpia = {
        ...data,
        food: foodFiltrado,
        // Nombres de modelos nuevos (para el filtro de modelo/versión)
        modelosNuevos: foodOriginal
          .filter(
            (item) =>
              normalizarTexto(item?.type || "") === "nuevos" &&
              coincideMarcaItem(item, marca),
          )
          .map((item) => item?.description || "")
          .filter(Boolean),
      };

      inventarioCacheRef.current[cacheKey] = dataLimpia;
      setTableData(dataLimpia);
    } catch (error) {
      console.error("Error al cargar accesorios y garantías:", error);
      setInventoryError(
        "No se pudo cargar el inventario de accesorios y garantías.",
      );
    } finally {
      setLoadingInventory(false);
    }
  }, [selectedAgencia, selectedVehicle.brand]);

  useEffect(() => {
    cargarAgencias();
  }, []);

  useEffect(() => {
    cargarInventarioAccesorios();
  }, [cargarInventarioAccesorios]);

  const seleccionarAgenciaYCargarAsesores = async (
    agId,
    nombreAgencia = "",
    asesorDetectado = "",
  ) => {
    if (!agId) {
      setSelectedAgencia("");
      setAsesores([]);
      return;
    }

    const id = String(agId);

    setSelectedAgencia(id);
    setAsesores([]);
    setSelectedAccessoryIds([]);

    setSelectedVehicle((prevState) => ({
      ...prevState,
      agency: nombreAgencia || prevState.agency || "",
    }));

    setFinanciamiento((prevState) => ({
      ...prevState,
      userId: "",
      asesor: asesorDetectado || "",
    }));

    await cargarAsesoresPorAgencia(id);
  };

  const handleAgenciaChange = async (e) => {
    const agId = e.target.value;

    const agenciaSeleccionada = agencias.find(
      (agencia) => String(agencia.ag_id) === String(agId),
    );

    await seleccionarAgenciaYCargarAsesores(
      agId,
      agenciaSeleccionada?.nombre || "",
    );
  };

  const handleAsesorChange = (e) => {
    const userId = e.target.value;

    const asesorSeleccionado = asesores.find(
      (asesor) => String(getAsesorId(asesor)) === String(userId),
    );

    setFinanciamiento((prevState) => ({
      ...prevState,
      userId,
      asesor: asesorSeleccionado
        ? getAsesorNombreDisplay(asesorSeleccionado)
        : "",
    }));
  };

  const handleClienteChange = (field, value) => {
    setCliente((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleVehicleChange = (field, value) => {
    setSelectedVehicle((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    if (["brand", "model", "version"].includes(field)) {
      setSelectedAccessoryIds([]);
    }
  };

  const handleFinanciamientoChange = (field, value) => {
    setFinanciamiento((prevState) => {
      const nextState = {
        ...prevState,
        [field]: value,
      };

      if (field === "incluyePlacas" && value !== "Si") {
        nextState.montoPlacas = "";
      }

      return nextState;
    });
  };

  const toggleAccessory = (item) => {
    const id = String(item.__manual_id);

    setSelectedAccessoryIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((itemId) => itemId !== id);
      }

      return [...prev, id];
    });
  };

  const obtenerTokenOpenIA = async () => {
    const formData = new FormData();
    formData.append("access_token", ACCESS_TOKEN);

    const res = await fetch(`${API_BASE_URL}obtener/token/openia`, {
      method: "POST",
      body: formData,
    });

    const data = await res.json();

    if (!res.ok || data.statusCode !== 200) {
      throw new Error(data.message || "No se pudo obtener el token de OpenIA");
    }

    const tokenOpenIA = data?.data?.token_openia?.access_token;

    if (!tokenOpenIA) {
      throw new Error("El servicio no devolvió access_token de OpenIA");
    }

    return tokenOpenIA;
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);

    const formData = new FormData();
    formData.append("apikey", "DPD8VZYKE82MX");
    formData.append("file", file);
    formData.append("language", "spa");
    formData.append("isOverlayRequired", "false");
    formData.append("detectOrientation", "true");
    formData.append("scale", "true");
    formData.append("OCREngine", "2");

    try {
      setLoading(true);

      const res = await fetch("https://apipro1.ocr.space/parse/image", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (!res.ok || data?.IsErroredOnProcessing) {
        const errorMessage = Array.isArray(data?.ErrorMessage)
          ? data.ErrorMessage.join(". ")
          : data?.ErrorMessage || "OCR.Space no pudo procesar el PDF";
        throw new Error(errorMessage);
      }

      const parsedResults = Array.isArray(data?.ParsedResults)
        ? data.ParsedResults
        : [];

      const text = parsedResults
        .map((page, index) => {
          const pageText = String(page?.ParsedText || "").trim();
          return pageText ? `--- PAGINA ${index + 1} ---\n${pageText}` : "";
        })
        .filter(Boolean)
        .join("\n\n");

      if (!text) {
        Swal.fire({
          icon: "warning",
          title: "PDF cargado",
          text: "El PDF se cargó correctamente, pero no se pudo leer automáticamente. Llena únicamente los datos que no aparezcan en la cotización.",
        });
        return;
      }

      // Conservamos saltos de línea: son esenciales para distinguir etiquetas,
      // tablas, plazos de seguro y mensualidades del financiamiento.
      const textoOCR = text
        .replace(/\u0000/g, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

      const datosReglas = extraerDatosCotizacionPorReglas(textoOCR);
      const tokenOpenIA = await obtenerTokenOpenIA();

      const responseOpenAI = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${tokenOpenIA}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "gpt-4o-mini",
            temperature: 0,
            max_tokens: 1400,
            response_format: { type: "json_object" },
            messages: [
              {
                role: "system",
                content:
                  "Eres un extractor estricto de cotizaciones automotrices mexicanas. No inventas datos y respetas la diferencia entre crédito, seguro, pago inicial, enganche y tablas de amortización.",
              },
              {
                role: "user",
                content: construirPromptCotizacion(textoOCR, datosReglas),
              },
            ],
          }),
        },
      );

      const dataOpenAI = await responseOpenAI.json();

      if (!responseOpenAI.ok) {
        throw new Error(
          dataOpenAI?.error?.message || "OpenAI no pudo analizar la cotización",
        );
      }

      const textoOrdenado = dataOpenAI?.choices?.[0]?.message?.content || "";
      const datosIA = textoOrdenado ? parseOpenAIJson(textoOrdenado) : {};
      const datos = combinarDatosCotizacion(datosIA, datosReglas);

      const nombreCompletoCliente = getValue(datos, [
        "Cliente",
        "cliente",
      ]);
      const nombreSeparadoFallback = separarNombreCompleto(nombreCompletoCliente);
      const nombreCliente = getValue(datos, ["Nombre", "nombre"]) || nombreSeparadoFallback.nombre;
      const apellidoPaterno =
        getValue(datos, [
          "ApellidoPaterno",
          "Apellido Paterno",
          "apellidoPaterno",
          "apellido_paterno",
        ]) || nombreSeparadoFallback.apellidoPaterno;
      const apellidoMaterno =
        getValue(datos, [
          "ApellidoMaterno",
          "Apellido Materno",
          "apellidoMaterno",
          "apellido_materno",
        ]) || nombreSeparadoFallback.apellidoMaterno;
      const telefono = getValue(datos, ["Telefono", "Teléfono", "telefono"]);
      const correo = getValue(datos, ["Correo", "correo", "Email", "email"]);
      const marca = getValue(datos, ["Marca", "marca"]);
      const modelo = getValue(datos, ["Modelo", "modelo"]);
      const anio = getValue(datos, ["Año", "Ano", "anio", "año"]);
      const version = getValue(datos, ["Version", "Versión", "version"]);
      const condicion = getValue(datos, [
        "TipoDeVehiculo",
        "TipoVehiculo",
        "tipoDeVehiculo",
      ]);

      const precioVehiculo = getValue(datos, [
        "PrecioVehiculo",
        "PrecioTotal",
        "Precio total",
        "precioVehiculo",
        "precioTotal",
      ]);
      const plazo = getValue(datos, ["PlazoMeses", "Plazo", "plazo"]);
      const enganche = getValue(datos, ["Enganche", "enganche"]);
      const mensualidad = getValue(datos, ["Mensualidad", "mensualidad"]);
      const tasa = getValue(datos, [
        "TasaDeInteres",
        "TazaDeInteres",
        "Tasa de Interes",
        "Tasa de Interés",
        "tasaDeInteres",
      ]);
      const financiera = normalizarFinanciera(
        getValue(datos, ["Financiera", "Banco", "banco"]),
      );
      const plan = getValue(datos, [
        "PlanDeFinanciamiento",
        "Plan de Financiamiento",
      ]);
      const agenciaOCR = getValue(datos, ["Agencia", "Distribuidor"]);
      const asesorOCR = getValue(datos, ["Asesor", "Vendedor"]);
      const metodoContacto = getValue(datos, [
        "MetodoContacto",
        "MétodoContacto",
        "metodoContacto",
        "FuenteOrigen",
        "Fuente Origen",
        "fuenteOrigen",
        "ComoTeEnteraste",
        "CómoTeEnteraste",
      ]);
      const origenCliente = getValue(datos, [
        "OrigenCliente",
        "Origen Cliente",
        "origenCliente",
        "Fuente",
        "fuente",
      ]);
      const aseguradora = getValue(datos, ["Aseguradora", "aseguradora"]);
      const montoSeguro = getValue(datos, ["MontoSeguro", "montoSeguro"]);
      const tipoCompraSeguro = getValue(datos, [
        "TipoCompraSeguro",
        "tipoCompraSeguro",
      ]);
      const montoComisionApertura = getValue(datos, [
        "MontoComisionApertura",
        "ComisionApertura",
        "montoComisionApertura",
      ]);
      const tipoComision = getValue(datos, ["TipoComision", "tipoComision"]);
      const incluyePlacas = getValue(datos, [
        "IncluyePlacas",
        "incluyePlacas",
        "AgregoPlacas",
        "AgregóPlacas",
      ]);
      const montoPlacas = getValue(datos, [
        "MontoPlacas",
        "montoPlacas",
        "Placas",
      ]);

      setCliente((prev) => ({
        ...prev,
        nombre: nombreCliente
          ? formatName(cleanName(nombreCliente))
          : prev.nombre,
        apellidoPaterno: apellidoPaterno
          ? formatName(cleanName(apellidoPaterno))
          : prev.apellidoPaterno,
        apellidoMaterno: apellidoMaterno
          ? formatName(cleanName(apellidoMaterno))
          : prev.apellidoMaterno,
        telefono: telefono || prev.telefono,
        correo: correo || prev.correo,
      }));

      setSelectedVehicle((prev) => ({
        ...prev,
        brand: marca || prev.brand,
        model: modelo || prev.model,
        ano: anio ? cleanNumberInput(anio) : prev.ano,
        version: version || prev.version,
        agency: agenciaOCR || prev.agency,
      }));

      if (condicion) {
        setCondicionVehiculo(
          normalizarTexto(condicion).includes("semi") ? "seminuevos" : "nuevos",
        );
      }

      // Si la cotización trae una agencia y coincide con las agencias permitidas
      // del usuario, se selecciona automáticamente.
      if (agenciaOCR && agencias.length) {
        const agenciaNormalizada = normalizarTexto(agenciaOCR);
        const agenciaMatch = agencias.find((item) => {
          const nombre = normalizarTexto(item?.nombre || "");
          return (
            nombre &&
            (nombre === agenciaNormalizada ||
              nombre.includes(agenciaNormalizada) ||
              agenciaNormalizada.includes(nombre))
          );
        });

        if (agenciaMatch?.ag_id) {
          await seleccionarAgenciaYCargarAsesores(
            agenciaMatch.ag_id,
            agenciaMatch.nombre || agenciaOCR,
            asesorOCR,
          );
        }
      }

      setFinanciamiento((prev) => ({
        ...prev,
        precioVehiculo: precioVehiculo
          ? cleanNumberInput(precioVehiculo)
          : prev.precioVehiculo,
        enganche: enganche ? cleanNumberInput(enganche) : prev.enganche,
        tasa:
          dependeAuto === "Arrendamiento" || dependeAuto === "Si"
            ? ""
            : tasa
              ? cleanNumberInput(tasa)
              : prev.tasa,
        mensualidad: mensualidad
          ? cleanNumberInput(mensualidad)
          : prev.mensualidad,
        plazo: plazo ? cleanNumberInput(plazo) : prev.plazo,
        financiera: FINANCIERA_OPTIONS.includes(financiera)
          ? financiera
          : prev.financiera,
        planFinanciamiento: plan || prev.planFinanciamiento,
        metodoContacto: METODO_CONTACTO_OPTIONS.includes(metodoContacto)
          ? metodoContacto
          : prev.metodoContacto,
        origenCliente: ORIGEN_CLIENTE_OPTIONS.includes(origenCliente)
          ? origenCliente
          : prev.origenCliente,
        aseguradora: aseguradora || prev.aseguradora,
        montoSeguro: montoSeguro
          ? cleanNumberInput(montoSeguro)
          : prev.montoSeguro,
        tipoCompraSeguro: tipoCompraSeguro || prev.tipoCompraSeguro,
        montoComisionApertura: montoComisionApertura
          ? cleanNumberInput(montoComisionApertura)
          : prev.montoComisionApertura,
        tipoComision: tipoComision || prev.tipoComision,
        incluyePlacas: incluyePlacas
          ? normalizarTexto(incluyePlacas).includes("si")
            ? "Si"
            : "No"
          : prev.incluyePlacas,
        montoPlacas: montoPlacas
          ? cleanNumberInput(montoPlacas)
          : prev.montoPlacas,
        asesor: asesorOCR || prev.asesor,
      }));

      const camposNoEncontrados = [];
      if (!nombreCliente) camposNoEncontrados.push("nombre del cliente");
      if (!apellidoPaterno) camposNoEncontrados.push("apellido paterno");
      if (!apellidoMaterno) camposNoEncontrados.push("apellido materno");
      if (!telefono) camposNoEncontrados.push("teléfono");
      if (!correo) camposNoEncontrados.push("correo");

      Swal.fire({
        icon: "success",
        title: "PDF leído",
        html: camposNoEncontrados.length
          ? `Se llenó la información detectada.<br><br><small>El PDF no proporcionó claramente: <strong>${camposNoEncontrados.join(", ")}</strong>.</small>`
          : "Se llenó automáticamente la información de la cotización. Revisa los datos antes de guardar.",
      });
    } catch (error) {
      console.error("Error al procesar OCR/OpenAI:", error);

      Swal.fire({
        icon: "warning",
        title: "PDF cargado",
        text: `El PDF se cargó, pero no se pudo procesar automáticamente. ${error?.message || ""}`.trim(),
      });
    } finally {
      setLoading(false);
    }
  };

  const validateStep = (currentStep = step) => {
    const missing = [];

    if (currentStep === 1) {
      if (!selectedFile) missing.push("PDF cotización");
      if (!cliente.nombre) missing.push("Nombre");
    }

    if (currentStep === 2) {
      if (!selectedVehicle.brand) missing.push("Marca");
      if (!selectedVehicle.model) missing.push("Modelo");
      if (!selectedVehicle.ano) missing.push("Año");
      if (!selectedVehicle.version) missing.push("Versión");
      if (!condicionVehiculo) missing.push("Condición");
      if (!selectedAgencia) missing.push("Agencia");
      if (!financiamiento.userId) missing.push("Asesor");
    }

    if (currentStep === 3) {
      if (!financiamiento.precioVehiculo) missing.push("Precio vehículo");

      if (dependeAuto !== "Si") {
        if (!financiamiento.enganche) missing.push("Enganche");

        if (dependeAuto === "No" && !financiamiento.tasa) {
          missing.push("Tasa");
        }

        if (!financiamiento.mensualidad) missing.push("Mensualidad");
        if (!financiamiento.plazo) missing.push("Plazo");
        if (!FINANCIERA_OPTIONS.includes(financiamiento.financiera))
          missing.push("Financiera");
      } else {
        // En contado, los MSI son únicamente para accesorios/garantías.
        if (!financiamiento.plazo) missing.push("Meses MSI");
        if (!FINANCIERA_OPTIONS.includes(financiamiento.financiera))
          missing.push("Financiera");
      }

      if (!financiamiento.metodoContacto)
        missing.push("¿Cómo te enteraste de nosotros?");
      if (!financiamiento.origenCliente) missing.push("Origen del cliente");
      if (
        financiamiento.incluyePlacas === "Si" &&
        !financiamiento.montoPlacas
      ) {
        missing.push("Monto placas");
      }
    }

    if (missing.length) {
      Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        html: `Falta llenar:<br><strong>${missing.join(", ")}</strong>`,
      });
      return false;
    }

    return true;
  };

  const handleNextStep = () => {
    if (!validateStep(step)) return;
    setStep((prev) => Math.min(prev + 1, 4));
  };

  const handlePreviousStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const resetManualSaleState = () => {
    setStep(1);
    setIsSubmitted(false);
    setSelectedFile(null);
    setSelectedAccessoryIds([]);
    setBusquedaAccesorio("");
    setTableData({ food: [] });
    setInventoryError("");
    setLoadingInventory(false);
    setSelectedAgencia("");
    setAsesores([]);
    setCargandoAsesores(false);
    setCliente(INITIAL_CLIENTE);
    setSelectedVehicle(INITIAL_SELECTED_VEHICLE);
    setFinanciamiento(INITIAL_FINANCIAMIENTO);
    setCondicionVehiculo("nuevos");
    setDependeAuto("No");
    setSolicitudTardia(false);
    setFechaTardia("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRegisterVehicle = async () => {
    if (isSubmitting || isSubmitted) return;

    for (let currentStep = 1; currentStep <= 3; currentStep += 1) {
      if (!validateStep(currentStep)) {
        setStep(currentStep);
        return;
      }
    }

    if (!selectedFile) {
      setStep(1);
      Swal.fire({
        icon: "warning",
        title: "PDF obligatorio",
        text: "Debes cargar el PDF de la cotización para guardar la venta manual.",
      });
      return;
    }

    // Solicitud tardía: se revalida al guardar por si el día ya cambió
    // mientras se llenaba el formulario.
    const enviarComoTardia = solicitudTardia && esDiaSolicitudTardia();

    if (solicitudTardia && !enviarComoTardia) {
      setSolicitudTardia(false);
      Swal.fire({
        icon: "warning",
        title: "Solicitud tardía no disponible",
        text: `Las solicitudes tardías solo se pueden registrar el día ${DIA_HABILITACION_SOLICITUD_TARDIA} de cada mes. Se desmarcó la opción; revisa y vuelve a guardar.`,
      });
      return;
    }

    if (enviarComoTardia) {
      const { max, etiqueta } = getRangoMesAnterior();

      if (fechaTardia && fechaTardia !== max) {
        setFechaTardia(max);
        Swal.fire({
          icon: "warning",
          title: "Fecha no permitida",
          text: `La solicitud tardía solo puede registrarse con fecha del ${etiqueta}. Se corrigió la fecha; vuelve a guardar.`,
        });
        return;
      }
    }

    const requestData = {
      access_token: ACCESS_TOKEN,

      solicitud_tardia: enviarComoTardia ? 1 : 0,
      fecha_tardia: enviarComoTardia
        ? fechaTardia || getRangoMesAnterior().max
        : "",

      nombre: cliente.nombre,
      apellido_paterno: cliente.apellidoPaterno,
      apellido_materno: cliente.apellidoMaterno,
      telefono: cliente.telefono,
      correo: cliente.correo,

      marca: selectedVehicle.brand,
      modelo: selectedVehicle.model,
      anio: selectedVehicle.ano,
      version: selectedVehicle.version,
      condicion: tipoAutoNormalizado,
      tipo_auto: tipoAutoNormalizado,
      agencia: selectedAgencyName,
      ag_id: selectedAgencia,
      asesor: asesorNombre,
      usuario_id: financiamiento.userId,

      depende_auto: dependeAuto,
      financiera: financiamiento.financiera,
      fuente: financiamiento.origenCliente,
      fuente_origen: financiamiento.metodoContacto,

      precio_vehiculo: precioVehiculoBase,
      precio_total: precioTotalFinal,
      financiamiento_meses: financiamiento.plazo,
      enganche: dependeAuto === "Si" ? "" : financiamiento.enganche,
      tasa:
        dependeAuto === "Arrendamiento" || dependeAuto === "Si"
          ? ""
          : financiamiento.tasa,
      mensualidad_estimada:
        dependeAuto === "Si"
          ? mensualidadContado
          : financiamiento.mensualidad,

      aseguradora: financiamiento.aseguradora,
      monto_seguro: financiamiento.montoSeguro,
      tipo_compra_seguro: financiamiento.tipoCompraSeguro,
      monto_comision_apertura: financiamiento.montoComisionApertura,
      tipo_comision: financiamiento.tipoComision,
      incluye_placas: financiamiento.incluyePlacas,
      monto_placas:
        financiamiento.incluyePlacas === "Si" ? financiamiento.montoPlacas : "",
      respuesta_prueba_manejo_cliente: financiamiento.respuestaPruebaManejo,

      plan_financiamiento: "Plan cliente",
      plan_accesorios: selectedAccessories,
      planes_seleccionados: selectedAccessories,
      planes_financiamiento: [planCliente],
      selected_car: selectedCarManual,
      order_cart: orderCartManual,

      venta_babilonia: "No",
    };

    const formData = new FormData();
    formData.append("pdf", selectedFile);

    Object.entries(requestData).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        formData.append(key, "");
        return;
      }

      formData.append(
        key,
        typeof value === "object" ? JSON.stringify(value) : String(value),
      );
    });

    try {
      setIsSubmitting(true);

      const response = await fetch(`${API_BASE_URL}solicitudes/register`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.statusCode === 200) {
        await Swal.fire({
          icon: "success",
          title: "Venta manual guardada",
          text: result?.data?.folio_venta
            ? `Folio generado: ${result.data.folio_venta}`
            : "La venta manual se guardó correctamente.",
        });

        resetManualSaleState();
        return;
      }

      Swal.fire({
        icon: "warning",
        title: "No se pudo guardar",
        text: result?.error?.description || "Revisa los datos enviados.",
      });
    } catch (error) {
      console.error("Error al conectar con la API:", error);

      Swal.fire({
        icon: "error",
        title: "Error de conexión",
        text: "Hubo un error al procesar la solicitud.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepHeader = () => (
    <div className="nav-wizards-container mb-3">
      <nav className="nav nav-wizards-3 mb-2 manual-sale-wizard">
        {[
          { number: 1, label: "PDF y cliente" },
          { number: 2, label: "Vehículo" },
          { number: 3, label: "Financiamiento" },
          { number: 4, label: "Plan cliente" },
        ].map((item) => (
          <div className="nav-item col" key={item.number}>
            <a
              className={`nav-link ${step >= item.number ? "completed" : ""} ${
                step === item.number ? "active" : ""
              }`}
              href="#/"
              onClick={(e) => {
                e.preventDefault();
                if (item.number < step) setStep(item.number);
              }}
            >
              <div className="nav-dot"></div>
              <div className="nav-title">{item.number}</div>
              <div className="nav-text">{item.label}</div>
            </a>
          </div>
        ))}
      </nav>
    </div>
  );

  return (
    <div>
      <style>{`
        .manual-sale-page .form-control,
        .manual-sale-page .form-select {
          border-radius: 10px;
        }

        .tardia-box {
          border: 1px solid rgba(0,0,0,.12);
          border-radius: 12px;
          padding: 12px 14px;
          margin-bottom: 16px;
          background: #f8f9fa;
          transition: all .15s ease;
        }

        .tardia-box.activa {
          border-color: #f2994a;
          background: rgba(242, 153, 74, .10);
        }

        .tardia-toggle {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          margin: 0;
          user-select: none;
        }

        .tardia-toggle input {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
        }

        .tardia-toggle .tardia-track {
          position: relative;
          flex: 0 0 auto;
          width: 44px;
          height: 24px;
          border-radius: 999px;
          background: #adb5bd;
          transition: background .15s ease;
        }

        .tardia-toggle .tardia-track::after {
          content: "";
          position: absolute;
          top: 3px;
          left: 3px;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 1px 3px rgba(0,0,0,.3);
          transition: transform .15s ease;
        }

        .tardia-toggle input:checked + .tardia-track {
          background: #f2994a;
        }

        .tardia-toggle input:checked + .tardia-track::after {
          transform: translateX(20px);
        }

        .tardia-toggle input:focus-visible + .tardia-track {
          outline: 2px solid #0d6efd;
          outline-offset: 2px;
        }

        .tardia-toggle input:disabled + .tardia-track {
          opacity: .5;
          cursor: not-allowed;
        }

        .manual-sale-hero {
          border: 1px solid rgba(13, 110, 253, .18);
          background: linear-gradient(135deg, rgba(13, 110, 253, .10), rgba(25, 135, 84, .08));
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 16px;
        }

        .manual-sale-summary-card {
          border: 1px solid rgba(0,0,0,.08);
          border-radius: 14px;
          background: #fff;
          padding: 14px;
          height: 100%;
          box-shadow: 0 3px 10px rgba(0,0,0,.04);
        }

        .manual-accessory-card {
          border: 1px solid rgba(0,0,0,.08);
          border-radius: 14px;
          padding: 14px;
          height: 100%;
          cursor: pointer;
          transition: all .15s ease;
          background: #fff;
        }

        .manual-accessory-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(0,0,0,.08);
        }

        .manual-accessory-card.is-selected {
          border-color: #0d6efd;
          box-shadow: 0 0 0 3px rgba(13,110,253,.14);
        }

        .manual-money-chip {
          border-radius: 999px;
          background: rgba(25, 135, 84, .10);
          color: #146c43;
          padding: 5px 10px;
          font-weight: 700;
          display: inline-block;
        }

        .manual-pill {
          border-radius: 999px;
          padding: 5px 10px;
          font-weight: 700;
          font-size: 12px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .manual-pill-blue {
          background: rgba(13, 110, 253, .10);
          color: #084298;
        }

        .manual-pill-green {
          background: rgba(25, 135, 84, .10);
          color: #146c43;
        }
      `}</style>

      {loading ? (
        <div className="loader-data">
          <Circles height="80" width="80" color="#4fa94d" />
        </div>
      ) : (
        <div className="manual-sale-page">
          <div className="mailbox">
            <div className="mailbox-toolbar mb-3">
              {tabs.map((tab) =>
                modules.includes(tab.module) ? (
                  <div className="mailbox-toolbar-item" key={tab.name}>
                    <Link
                      to={tab.to}
                      className={`mailbox-toolbar-link ${
                        location.pathname === tab.to ? "active" : ""
                      }`}
                    >
                      {tab.name}
                    </Link>
                  </div>
                ) : null,
              )}
            </div>

            <div className="container-fluid">
              <Card>
                <CardHeader className="with-btn">
                  CREAR SOLICITUD
                  <CardHeaderBtn />
                </CardHeader>

                <CardBody>
                  {renderStepHeader()}

                  <div className="row g-3 mb-3">
                    <div className="col-md-3">
                      <div className="manual-sale-summary-card">
                        <div className="text-muted small">Vehículo</div>
                        <strong>{formatCurrency(precioVehiculoBase)}</strong>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="manual-sale-summary-card">
                        <div className="text-muted small">Accesorios</div>
                        <strong>{formatCurrency(totalAccesorios)}</strong>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="manual-sale-summary-card">
                        <div className="text-muted small">Total</div>
                        <strong>{formatCurrency(precioTotalFinal)}</strong>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="manual-sale-summary-card">
                        <div className="text-muted small">
                          {dependeAuto === "Si"
                            ? "MSI adicionales"
                            : "Mensualidad"}
                        </div>
                        <strong>
                          {formatCurrency(
                            dependeAuto === "Si"
                              ? mensualidadContado
                              : financiamiento.mensualidad,
                          )}
                        </strong>
                        {dependeAuto === "Si" && (
                          <div className="small text-muted mt-1">
                            {financiamiento.plazo
                              ? `${financiamiento.plazo} MSI`
                              : "Selecciona meses MSI"}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <Card>
                    <CardBody>
                      {step === 1 && (
                        <div>
                          <h5 className="black-text mb-3">PDF y cliente</h5>

                          <div className="alert alert-warning">
                            El PDF de cotización es obligatorio. Si el OCR
                            falla, el PDF queda cargado y puedes llenar los
                            datos manualmente.
                          </div>

                          {diaTardiaHabilitado && (
                            <div
                              className={`tardia-box ${
                                solicitudTardia ? "activa" : ""
                              }`}
                            >
                              <label
                                className="tardia-toggle black-text"
                                htmlFor="solicitudTardia"
                              >
                                <input
                                  type="checkbox"
                                  id="solicitudTardia"
                                  checked={solicitudTardia}
                                  disabled={isSubmitting || isSubmitted}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setSolicitudTardia(checked);
                                    if (checked && !fechaTardia) {
                                      setFechaTardia(rangoTardia.max);
                                    }
                                  }}
                                />
                                <span className="tardia-track" />
                                <span>
                                  <strong>Solicitud tardía</strong> (corresponde a{" "}
                                  {rangoTardia.nombreMes})
                                  {solicitudTardia && (
                                    <span className="badge bg-warning text-dark ms-2">
                                      Activada
                                    </span>
                                  )}
                                </span>
                              </label>
                              <div className="small text-muted mt-1">
                                Disponible solo hoy. Úsala para solicitudes del mes
                                anterior que no se capturaron a tiempo; se contarán el{" "}
                                {rangoTardia.etiqueta}.
                              </div>

                              {solicitudTardia && (
                                <div className="row mt-2">
                                  <div className="col-sm-6 col-lg-3">
                                    <label htmlFor="fechaTardia" className="black-text">
                                      Fecha a la que corresponde
                                    </label>
                                    <input
                                      id="fechaTardia"
                                      type="date"
                                      className="form-control"
                                      min={rangoTardia.min}
                                      max={rangoTardia.max}
                                      value={fechaTardia}
                                      disabled={isSubmitting || isSubmitted}
                                      onChange={(e) => {
                                        // Solo se acepta el último día del mes anterior
                                        if (e.target.value === rangoTardia.max) {
                                          setFechaTardia(e.target.value);
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="row">
                            <div className="col-lg-4 mb-3">
                              <label
                                htmlFor="dependeAuto"
                                className="black-text"
                              >
                                Tipo de operación{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <select
                                id="dependeAuto"
                                className="form-control"
                                value={dependeAuto}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setDependeAuto(value);

                                  setFinanciamiento((prev) => {
                                    if (value === "Si") {
                                      return {
                                        ...prev,
                                        enganche: "",
                                        tasa: "",
                                        plazo: ["3", "6", "9"].includes(
                                          String(prev.plazo),
                                        )
                                          ? String(prev.plazo)
                                          : "3",
                                      };
                                    }

                                    if (value === "Arrendamiento") {
                                      return {
                                        ...prev,
                                        tasa: "",
                                      };
                                    }

                                    return prev;
                                  });
                                }}
                                disabled={isSubmitted}
                              >
                                <option value="No">Crédito</option>
                                <option value="Si">Contado</option>
                                <option value="Arrendamiento">
                                  Arrendamiento
                                </option>
                              </select>
                              <div className="small text-muted mt-1">
                                Crédito, contado o arrendamiento.
                              </div>
                            </div>

                            <div className="col-lg-8 mb-3">
                              <label htmlFor="pdfFile" className="black-text">
                                PDF Cotización{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="application/pdf"
                                onChange={handleFileChange}
                                className="form-control"
                                id="pdfFile"
                                disabled={isSubmitted}
                              />
                              {selectedFile && (
                                <div className="small text-success mt-1">
                                  Archivo cargado: {selectedFile.name}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-4 mb-3">
                              <label className="black-text">Nombre(s) *</label>
                              <input
                                type="text"
                                className="form-control"
                                value={cliente.nombre}
                                onChange={(e) =>
                                  handleClienteChange("nombre", e.target.value)
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-4 mb-3">
                              <label className="black-text">
                                Apellido paterno *
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={cliente.apellidoPaterno}
                                onChange={(e) =>
                                  handleClienteChange(
                                    "apellidoPaterno",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-4 mb-3">
                              <label className="black-text">
                                Apellido materno *
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={cliente.apellidoMaterno}
                                onChange={(e) =>
                                  handleClienteChange(
                                    "apellidoMaterno",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-6 mb-3">
                              <label className="black-text">Teléfono *</label>
                              <input
                                type="text"
                                className="form-control"
                                value={cliente.telefono}
                                onChange={(e) =>
                                  handleClienteChange(
                                    "telefono",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>
                            <div className="col-lg-6 mb-3">
                              <label className="black-text">
                                Correo electrónico *
                              </label>
                              <input
                                type="email"
                                className="form-control"
                                value={cliente.correo}
                                onChange={(e) =>
                                  handleClienteChange("correo", e.target.value)
                                }
                                disabled={isSubmitted}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {step === 2 && (
                        <div>
                          <h5 className="black-text mb-3">
                            Vehículo, agencia y asesor
                          </h5>

                          <div className="row">
                            <div className="col-lg-3 mb-3">
                              <label className="black-text">Condición *</label>
                              <select
                                className="form-control"
                                value={condicionVehiculo}
                                onChange={(e) => {
                                  setCondicionVehiculo(e.target.value);
                                  setSelectedAccessoryIds([]);
                                }}
                                disabled={isSubmitted}
                              >
                                <option value="nuevos">Nuevo</option>
                                <option value="seminuevos">Seminuevo</option>
                              </select>
                            </div>

                            <div className="col-lg-3 mb-3">
                              <label className="black-text">Marca *</label>
                              <input
                                type="text"
                                className="form-control"
                                value={selectedVehicle.brand}
                                onChange={(e) =>
                                  handleVehicleChange("brand", e.target.value)
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-3 mb-3">
                              <label className="black-text">Modelo *</label>
                              <input
                                type="text"
                                className="form-control"
                                value={selectedVehicle.model}
                                onChange={(e) =>
                                  handleVehicleChange("model", e.target.value)
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-3 mb-3">
                              <label className="black-text">Año *</label>
                              <input
                                type="text"
                                className="form-control"
                                value={selectedVehicle.ano}
                                onChange={(e) =>
                                  handleVehicleChange("ano", e.target.value)
                                }
                                disabled={isSubmitted}
                              />
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-12 mb-3">
                              <label className="black-text">Versión *</label>
                              <input
                                type="text"
                                className="form-control"
                                value={selectedVehicle.version}
                                onChange={(e) =>
                                  handleVehicleChange("version", e.target.value)
                                }
                                disabled={isSubmitted}
                              />
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-6 mb-3">
                              <label className="black-text">Agencia *</label>
                              <select
                                className="form-control custom-select"
                                onChange={handleAgenciaChange}
                                value={selectedAgencia}
                                disabled={isSubmitted}
                              >
                                <option value="">Seleccione agencia</option>
                                {agencias.map((agencia) => (
                                  <option
                                    key={agencia.ag_id}
                                    value={agencia.ag_id}
                                  >
                                    {agencia.nombre}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="col-lg-6 mb-3">
                              <label className="black-text">Asesor *</label>
                              <select
                                className="form-control custom-select"
                                value={financiamiento.userId || ""}
                                onChange={handleAsesorChange}
                                disabled={
                                  !selectedAgencia ||
                                  cargandoAsesores ||
                                  isSubmitted
                                }
                              >
                                <option value="">
                                  {!selectedAgencia
                                    ? "Primero selecciona una agencia"
                                    : cargandoAsesores
                                      ? "Cargando asesores..."
                                      : "Seleccione asesor"}
                                </option>
                                {asesoresOrdenados.map((asesor) => {
                                  const asesorId = getAsesorId(asesor);
                                  const asesorNombre =
                                    getAsesorNombreDisplay(asesor);

                                  return (
                                    <option key={asesorId} value={asesorId}>
                                      {asesorNombre}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {step === 3 && (
                        <div>
                          <h5 className="black-text mb-3">
                            Financiamiento, seguro y cargos
                          </h5>

                          <div className="row">
                            <div className="col-lg-4 mb-3">
                              <label className="black-text">
                                Precio vehículo *
                              </label>
                              <MoneyInput
                                value={financiamiento.precioVehiculo}
                                onChange={(value) =>
                                  handleFinanciamientoChange(
                                    "precioVehiculo",
                                    value,
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            {dependeAuto !== "Si" ? (
                              <>
                                <div className="col-lg-4 mb-3">
                                  <label className="black-text">
                                    Enganche *
                                  </label>
                                  <MoneyInput
                                    value={financiamiento.enganche}
                                    onChange={(value) =>
                                      handleFinanciamientoChange(
                                        "enganche",
                                        value,
                                      )
                                    }
                                    disabled={isSubmitted}
                                  />
                                </div>

                                <div className="col-lg-4 mb-3">
                                  <label className="black-text">
                                    Mensualidad *
                                  </label>
                                  <MoneyInput
                                    value={financiamiento.mensualidad}
                                    onChange={(value) =>
                                      handleFinanciamientoChange(
                                        "mensualidad",
                                        value,
                                      )
                                    }
                                    disabled={isSubmitted}
                                  />
                                </div>
                              </>
                            ) : (
                              <div className="col-lg-8 mb-3">
                                <label className="black-text">
                                  Mensualidad MSI de adicionales
                                </label>
                                <div className="form-control bg-light">
                                  {formatCurrency(mensualidadContado)}
                                </div>
                                <div className="small text-muted mt-1">
                                  Se calcula a 0% únicamente sobre los
                                  accesorios y garantías del Plan cliente.
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="row">
                            {dependeAuto === "Si" ? (
                              <div className="col-lg-4 mb-3">
                                <label className="black-text">
                                  Tipo de operación
                                </label>
                                <div className="form-control bg-light">
                                  Contado
                                </div>
                                <div className="small text-muted mt-1">
                                  No aplica enganche, tasa ni financiera.
                                </div>
                              </div>
                            ) : dependeAuto === "Arrendamiento" ? (
                              <div className="col-lg-4 mb-3">
                                <label className="black-text">
                                  Tipo de operación
                                </label>
                                <div className="form-control bg-light">
                                  Arrendamiento
                                </div>
                                <div className="small text-muted mt-1">
                                  En arrendamiento no se captura tasa.
                                </div>
                              </div>
                            ) : (
                              <div className="col-lg-4 mb-3">
                                <label className="black-text">Tasa (%) *</label>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className="form-control"
                                  value={formatThousands(financiamiento.tasa)}
                                  onChange={(e) =>
                                    handleFinanciamientoChange(
                                      "tasa",
                                      cleanNumberInput(e.target.value),
                                    )
                                  }
                                  disabled={isSubmitted}
                                />
                              </div>
                            )}

                            <div className="col-lg-4 mb-3">
                              <label className="black-text">
                                {dependeAuto === "Si"
                                  ? "Meses MSI *"
                                  : "Plazo *"}
                              </label>
                              <select
                                className="form-control"
                                value={financiamiento.plazo}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "plazo",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">
                                  {dependeAuto === "Si"
                                    ? "Selecciona meses MSI"
                                    : "Selecciona plazo"}
                                </option>

                                {dependeAuto === "Si" ? (
                                  <>
                                    <option value="3">3 meses</option>
                                    <option value="6">6 meses</option>
                                    <option value="9">9 meses</option>
                                  </>
                                ) : (
                                  <>
                                    <option value="2">2 meses</option>
                                    <option value="3">3 meses</option>
                                    <option value="6">6 meses</option>
                                    <option value="9">9 meses</option>
                                    <option value="12">12 meses</option>
                                    <option value="24">24 meses</option>
                                    <option value="36">36 meses</option>
                                    <option value="48">48 meses</option>
                                    <option value="60">60 meses</option>
                                    <option value="72">72 meses</option>
                                    <option value="84">84 meses</option>
                                  </>
                                )}
                              </select>
                              {dependeAuto === "Si" && (
                                <div className="small text-muted mt-1">
                                  En contado los MSI son únicamente para
                                  accesorios y garantías.
                                </div>
                              )}
                            </div>

                            <div className="col-lg-4 mb-3">
                              <label className="black-text">
                                Financiera *
                              </label>
                              <select
                                className="form-control"
                                value={financiamiento.financiera}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "financiera",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">Selecciona financiera</option>
                                {FINANCIERA_OPTIONS.map((f) => (
                                  <option key={f} value={f}>
                                    {f}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-6 mb-3">
                              <label
                                htmlFor="metodoContacto"
                                className="new-font form-label black-text"
                              >
                                ¿Cómo te enteraste de nosotros? *
                              </label>
                              <select
                                id="metodoContacto"
                                className="new-font form-select"
                                value={financiamiento.metodoContacto}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "metodoContacto",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">Selecciona una opción</option>
                                {METODO_CONTACTO_OPTIONS.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="col-lg-6 mb-3">
                              <label
                                htmlFor="origenCliente"
                                className="new-font form-label black-text"
                              >
                                Origen del Cliente *
                              </label>
                              <select
                                id="origenCliente"
                                className="new-font form-select"
                                value={financiamiento.origenCliente}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "origenCliente",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">Selecciona el origen</option>
                                {ORIGEN_CLIENTE_OPTIONS.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-6 mb-3">
                              <label className="black-text">Aseguradora</label>
                              <input
                                type="text"
                                className="form-control"
                                value={financiamiento.aseguradora}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "aseguradora",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-6 mb-3">
                              <label className="black-text">Monto seguro</label>
                              <MoneyInput
                                value={financiamiento.montoSeguro}
                                onChange={(value) =>
                                  handleFinanciamientoChange(
                                    "montoSeguro",
                                    value,
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-3 mb-3">
                              <label className="black-text">
                                Tipo compra seguro
                              </label>
                              <select
                                className="form-control"
                                value={financiamiento.tipoCompraSeguro}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "tipoCompraSeguro",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">Selecciona</option>
                                <option value="Contado">Contado</option>
                                <option value="Financiado">Financiado</option>
                              </select>
                            </div>

                            <div className="col-lg-3 mb-3">
                              <label className="black-text">
                                Comisión apertura
                              </label>
                              <MoneyInput
                                value={financiamiento.montoComisionApertura}
                                onChange={(value) =>
                                  handleFinanciamientoChange(
                                    "montoComisionApertura",
                                    value,
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-3 mb-3">
                              <label className="black-text">
                                Tipo comisión
                              </label>
                              <select
                                className="form-control"
                                value={financiamiento.tipoComision}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "tipoComision",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">Selecciona</option>
                                <option value="Contado">Contado</option>
                                <option value="Financiado">Financiado</option>
                              </select>
                            </div>

                            <div className="col-lg-3 mb-3">
                              <label className="black-text">
                                ¿Se agregaron placas?
                              </label>
                              <select
                                className="form-control"
                                value={financiamiento.incluyePlacas}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "incluyePlacas",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="No">No</option>
                                <option value="Si">Sí</option>
                              </select>
                            </div>
                          </div>

                          {financiamiento.incluyePlacas === "Si" && (
                            <div className="row">
                              <div className="col-lg-4 mb-3">
                                <label className="black-text">
                                  Monto placas *
                                </label>
                                <MoneyInput
                                  value={financiamiento.montoPlacas}
                                  onChange={(value) =>
                                    handleFinanciamientoChange(
                                      "montoPlacas",
                                      value,
                                    )
                                  }
                                  disabled={isSubmitted}
                                />
                                {montoPlacasSugerido && (
                                  <small className="text-muted d-block mt-1">
                                    Sugerido para este modelo:{" "}
                                    {formatCurrency(Number(montoPlacasSugerido))}
                                  </small>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="row">
                            <div className="col-lg-4 mb-3">
                              <label className="black-text">
                                Respuesta prueba manejo
                              </label>
                              <select
                                className="form-control"
                                value={financiamiento.respuestaPruebaManejo}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "respuestaPruebaManejo",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">Sin respuesta</option>
                                <option value="Si">Sí</option>
                                <option value="No">No</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {step === 4 && (
                        <div>
                          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                            <div>
                              <h5 className="black-text mb-1">Plan cliente</h5>
                              <div className="text-muted">
                                Accesorios y garantías cargados desde el
                                inventario vigente.
                              </div>
                            </div>

                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={cargarInventarioAccesorios}
                              disabled={loadingInventory || isSubmitted}
                            >
                              {loadingInventory
                                ? "Cargando..."
                                : "Recargar inventario"}
                            </button>
                          </div>

                          {!selectedAgencia || !selectedVehicle.brand ? (
                            <div className="alert alert-info">
                              Selecciona agencia y marca para cargar accesorios
                              y garantías.
                            </div>
                          ) : null}

                          {inventoryError && (
                            <div className="alert alert-warning">
                              {inventoryError}
                            </div>
                          )}

                          <div className="alert alert-primary">
                            <strong>Plan:</strong> Plan cliente
                            <br />
                            <strong>Seleccionados:</strong>{" "}
                            {selectedAccessories.length}
                            <br />
                            <strong>Total accesorios:</strong>{" "}
                            {formatCurrency(totalAccesorios)}
                            <br />
                            <strong>Precio vehículo:</strong>{" "}
                            {formatCurrency(precioVehiculoBase)}
                            <br />
                            <strong>Precio total final:</strong>{" "}
                            {formatCurrency(precioTotalFinal)}
                          </div>

                          <div className="mb-3">
                            <label
                              htmlFor="busquedaAccesorio"
                              className="black-text"
                            >
                              Buscar accesorio o garantía
                            </label>
                            <div className="d-flex gap-2">
                              <input
                                id="busquedaAccesorio"
                                type="text"
                                className="form-control"
                                placeholder="Ej. Tatuado Autopartes, llantas, garantía..."
                                value={busquedaAccesorio}
                                onChange={(e) =>
                                  setBusquedaAccesorio(e.target.value)
                                }
                                disabled={isSubmitted}
                              />
                              {busquedaAccesorio && !isSubmitted && (
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => setBusquedaAccesorio("")}
                                >
                                  Limpiar
                                </button>
                              )}
                            </div>
                            <div className="small text-muted mt-1">
                              Mostrando {accesoriosGarantiasVisibles.length} de{" "}
                              {filteredAccessoriesAndWarranties.length} opciones.
                              Si escribes el nombre completo, se muestra la
                              coincidencia exacta. También acepta búsquedas parciales
                              y pequeños errores de escritura.
                            </div>
                          </div>

                          {loadingInventory ? (
                            <div className="text-center py-4">
                              <Circles height="60" width="60" color="#4fa94d" />
                            </div>
                          ) : (
                            <div className="row">
                              {accesoriosGarantiasVisibles.length ? (
                                accesoriosGarantiasVisibles.map((item) => {
                                  const itemId = String(item.__manual_id);
                                  const isSelected =
                                    selectedAccessoryIds.includes(itemId);
                                  const normalized =
                                    normalizeAccessoryForSave(item);

                                  return (
                                    <div className="col-md-4 mb-3" key={itemId}>
                                      <div
                                        className={`manual-accessory-card ${
                                          isSelected ? "is-selected" : ""
                                        }`}
                                        onClick={() => {
                                          if (!isSubmitted)
                                            toggleAccessory(item);
                                        }}
                                      >
                                        <div className="d-flex justify-content-between gap-2 mb-2">
                                          <span
                                            className={`badge ${
                                              normalizarTexto(item.type) ===
                                              "garantias"
                                                ? "bg-success"
                                                : "bg-primary"
                                            }`}
                                          >
                                            {normalizarTexto(item.type) ===
                                            "garantias"
                                              ? "Garantía"
                                              : "Accesorio"}
                                          </span>

                                          {isSelected ? (
                                            <span className="badge bg-dark">
                                              Seleccionado
                                            </span>
                                          ) : (
                                            <span className="badge bg-secondary">
                                              Disponible
                                            </span>
                                          )}
                                        </div>

                                        <h6 className="mb-2">
                                          {normalized.title}
                                        </h6>
                                        <p className="text-muted small mb-3">
                                          {normalized.description ||
                                            "Sin descripción"}
                                        </p>
                                        <span className="manual-money-chip">
                                          {formatCurrency(normalized.price)}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="col-12">
                                  <div className="alert alert-light border">
                                    {busquedaAccesorio
                                      ? `No se encontraron accesorios o garantías parecidos a "${busquedaAccesorio}".`
                                      : "No hay accesorios o garantías disponibles para esta agencia, marca, modelo y condición."}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="text-center mt-4">
                        {step > 1 && (
                          <button
                            type="button"
                            className="btn btn-secondary mx-2"
                            onClick={handlePreviousStep}
                            disabled={isSubmitting}
                          >
                            Anterior
                          </button>
                        )}

                        {step < 4 && (
                          <button
                            type="button"
                            className="btn btn-primary mx-2"
                            onClick={handleNextStep}
                            disabled={isSubmitting}
                          >
                            Siguiente
                          </button>
                        )}

                        {step === 4 && (
                          <button
                            type="button"
                            className="btn btn-success mx-2"
                            onClick={handleRegisterVehicle}
                            disabled={isSubmitting || isSubmitted}
                          >
                            {isSubmitting
                              ? "Guardando..."
                              : isSubmitted
                                ? "Guardado"
                                : "Guardar venta manual"}
                          </button>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateRequest;